<template>
  <div class="table-editor-container app-page app-page--fill">
    <el-card>
      
      <div class="main-content" ref="mainContentRef">
        <!-- 左侧列表 -->
        <div class="left-panel" ref="leftPanelRef" :style="{ width: leftPanelWidth + 'px' }">
          <div class="list-header">
            <el-button v-if="can('新增')" type="primary" @click="addNewTable">
              <el-icon><Plus /></el-icon> 新增
            </el-button>
            <el-button
              type="primary"
              circle
              size="small"
              class="app-refresh-btn"
              @click="loadTableList"
              :loading="listLoading"
              :icon="Refresh"
              style="margin-left: auto;"
              title="刷新"
            />
          </div>
          
          <div class="table-list-wrapper">
            <!-- DynamicTable 的编辑图标在此处实际用于进入查看模式 -->
            <DynamicTable 
              ref="dynamicTableRef"
              :data="tableListData" 
              :model="tableModel"
              :loading="listLoading"
              :show-column-selector="false"
              :show-export="false"
              :show-edit-button="can('查看')"
              :show-delete-button="can('删除')"
              :pagination="pagination"
              :active-row-key="activeTableRowKey"
              @edit="handleTableEdit"
              @delete="handleTableDelete"
              @filter="handleFilter"
              @page-change="handlePageChange"
              @page-size-change="handlePageSizeChange"
            >
              <template #action="{ row }">
                <el-button
                  type="success"
                  size="small"
                  :icon="CopyDocument"
                  plain
                  title="复制"
                  @click="copyTable(row)"
                />
              </template>
            </DynamicTable>
          </div>
        </div>
        
        <!-- 拖拽分隔条 -->
        <div class="resize-handle" @mousedown="startResize"></div>
        
        <!-- 右侧编辑区域 -->
        <div class="right-panel">
          <div v-if="currentTable" class="edit-area" v-loading="loading" element-loading-text="加载中...">
            <!-- 编辑按钮区域 -->
            <div class="edit-mode-control">
              <el-button
                v-if="!isEditMode && can('编辑')"
                type="primary"
                @click="enterEditMode"
                :icon="Edit"
              >
                编辑
              </el-button>
              <el-button
                v-if="isEditMode"
                type="info"
                @click="exitEditMode"
                :icon="Close"
              >
                取消编辑
              </el-button>
              <el-button
                v-if="currentTableId && !isEditMode && can('打印')"
                type="warning"
                :icon="Printer"
                @click="handlePrintList"
              >
                原始记录预览
              </el-button>
            </div>
            
            <!-- 名称输入区域 -->
            <div class="table-name-section">
              <div class="form-item" :class="{ 'form-item--error': showNameError }">
                <span class="section-label section-label--required">名称：</span>
                <div class="form-control">
                  <el-input
                    v-model="currentTable.name"
                    placeholder="请输入表格名称"
                    :readonly="!isEditMode"
                  />
                  <div v-if="showNameError" class="form-error-text">请输入名称</div>
                </div>
              </div>
              <div class="form-item" :class="{ 'form-item--error': showControlNumberError }">
                <span class="section-label section-label--required">受控编号：</span>
                <div class="form-control">
                  <el-input
                    v-model="currentTable.controlNumber"
                    placeholder="请输入受控编号"
                    :readonly="!isEditMode"
                  />
                  <div v-if="showControlNumberError" class="form-error-text">请输入受控编号</div>
                </div>
              </div>
              <div class="form-item" :class="{ 'form-item--error': showNumberSuffixError }">
                <span class="section-label section-label--required">编号后缀：</span>
                <div class="form-control">
                  <el-input
                    v-model="currentTable.numberSuffix"
                    placeholder="请输入编号后缀"
                    :readonly="!isEditMode"
                  />
                  <div v-if="showNumberSuffixError" class="form-error-text">请输入编号后缀</div>
                </div>
              </div>
              <div class="form-item form-item--date" :class="{ 'form-item--error': showImplementDateError }">
                <span class="section-label section-label--required">实施日期：</span>
                <div class="form-control">
                  <el-date-picker
                    v-model="currentTable.implementDate"
                    type="date"
                    placeholder="选择日期"
                    :disabled="!isEditMode"
                    value-format="YYYY-MM-DD"
                  />
                  <div v-if="showImplementDateError" class="form-error-text">请选择实施日期</div>
                </div>
              </div>
              <div class="form-item">
                <span class="section-label">数据类型：</span>
                <el-select
                  v-model="currentTable.type"
                  placeholder="类型"
                  :disabled="!isEditMode" 
                >
                  <el-option label="表格" value="表格" />
                  <el-option label="图片" value="图片" />
                </el-select>
              </div>
              <div class="form-item">
                <span class="section-label">构件类型：</span>
                <el-select
                  v-model="currentTable.detectionObjectType"
                  placeholder="类型"
                  :disabled="!isEditMode"
                  clearable
                >
                  <el-option label="默认" value="默认" />
                  <el-option label="墙" value="墙" />
                  <el-option label="板" value="板" />
                  <el-option label="梁" value="梁" />
                  <el-option label="柱" value="柱" />
                  <el-option label="地面" value="地面" />
                </el-select>
              </div>
              <div class="form-item">
                <span class="section-label">设备类型：</span>
                <el-select
                  v-model="currentTable.deviceType"
                  placeholder="请选择设备类型"
                  :disabled="!isEditMode"
                  :loading="deviceTypeLoading"
                  clearable
                  filterable
                >
                  <el-option
                    v-for="item in deviceTypeOptions"
                    :key="item.Id"
                    :label="item.名称"
                    :value="item.名称"
                  />
                </el-select>
              </div>
              <div class="form-item">
                <span class="section-label">是否计算：</span>
                <el-switch
                  v-model="currentTable.isCalculate"
                  :disabled="!isEditMode"
                  active-text="是"
                  inactive-text="否"
                />
              </div>
              
              <div class="form-item">
                <span class="section-label">打印方向：</span>
                <el-switch
                  v-model="currentTable.printOrientation"
                  :disabled="!isEditMode"
                  active-text="横向"
                  inactive-text="竖向"
                />
              </div>
              <div class="form-item form-item--remark">
                <span class="section-label">说明：</span>
                <el-input
                  v-model="currentTable.remark"
                  type="textarea"
                  :autosize="{ minRows: 3, maxRows: 6 }"
                  placeholder="请输入说明"
                  :readonly="!isEditMode"
                />
              </div>
            </div>
            
            <!-- 非数据表区域字段配置 与 图片上传区域 -->
            <div class="attributes-container">
              <div class="special-attributes">
                <div class="attributes-title">非数据表区域字段配置</div>
                <div class="attributes-subtitle">非数据表区域字段（即表格区域外需要录入的字段）</div>
                <div class="attributes-columns">
                  <!-- 左侧：全局属性 -->
                  <div class="attributes-column global-attributes">
                    <div class="column-title">
                      <el-icon><Collection /></el-icon>
                      <span>全局</span>
                    </div>
                    <div class="column-desc">此原始记录所有数据公用的字段</div>
                    <div class="attributes-list">
                      <div
                        v-for="attr in globalAttributesWithId"
                        :key="attr.id"
                        class="attribute-item"
                        :class="{ 'dragging': dragIndex === attr.originalIndex, 'drag-over': dragOverIndex === attr.originalIndex && dragIndex !== attr.originalIndex }"
                        :draggable="isEditMode"
                        @dragstart="handleDragStart($event, attr.originalIndex)"
                        @dragend="handleDragEnd"
                        @dragover.prevent="handleDragOver($event, attr.originalIndex)"
                        @dragleave="handleDragLeave"
                        @drop.prevent="handleDrop($event, attr.originalIndex)"
                      >
                        <div class="drag-handle" v-if="isEditMode" title="拖拽排序">
                          <el-icon><Rank /></el-icon>
                        </div>
                        <div class="attribute-label">字段：</div>
                        <el-input
                          :model-value="attr.title"
                          placeholder="请输入标题"
                          :readonly="!isEditMode"
                          @update:model-value="(value: string) => updateSpecialAttribute(attr.originalIndex, value)"
                          class="attribute-input"
                          :style="{ width: isEditMode ? '130px' : (attr.options.length > 0 ? '110px' : '170px') }"
                        />
                        <div v-if="!isEditMode && attr.options.length > 0" class="attribute-options-display">
                          <span class="options-label">选项：</span>
                          <el-tag
                            v-for="(opt, optIndex) in attr.options"
                            :key="optIndex"
                            size="small"
                            type="info"
                            class="option-tag"
                          >
                            {{ opt }}
                          </el-tag>
                        </div>
                        <el-button
                          v-if="isEditMode"
                          type="primary"
                          size="small"
                          @click="openOptionsDialog(attr.originalIndex)"
                          class="attribute-options-btn"
                        >
                          选项{{ attr.options.length > 0 ? `(${attr.options.length})` : '' }}
                        </el-button>
                        <el-button
                          v-if="isEditMode"
                          type="danger"
                          size="small"
                          @click="deleteSpecialAttribute(attr.originalIndex)"
                          class="attribute-delete-btn"
                        >
                          <el-icon><Delete /></el-icon>
                        </el-button>
                        <el-tooltip v-if="isEditMode" content="切换为单条属性" placement="top">
                          <el-button
                            type="info"
                            size="small"
                            link
                            class="attribute-switch-btn"
                            @click="toggleAttributeScope(attr.originalIndex)"
                          >
                            单条→
                          </el-button>
                        </el-tooltip>
                      </div>
                    </div>
                    <el-button
                      v-if="isEditMode"
                      type="primary"
                      plain
                      size="small"
                      @click="addSpecialAttribute(false)"
                      class="add-attribute-btn"
                    >
                      <el-icon><Plus /></el-icon> 添加全局字段
                    </el-button>
                  </div>

                  <!-- 右侧：单条属性 -->
                  <div class="attributes-column single-attributes">
                    <div class="column-title">
                      <el-icon><DocumentCopy /></el-icon>
                      <span>单条</span>
                    </div>
                    <div class="column-desc">此原始记录单次添加时需要录入的字段</div>
                    <div class="attributes-list">
                      <div
                        v-for="attr in singleAttributesWithId"
                        :key="attr.id"
                        class="attribute-item"
                        :class="{ 'dragging': dragIndex === attr.originalIndex, 'drag-over': dragOverIndex === attr.originalIndex && dragIndex !== attr.originalIndex }"
                        :draggable="isEditMode"
                        @dragstart="handleDragStart($event, attr.originalIndex)"
                        @dragend="handleDragEnd"
                        @dragover.prevent="handleDragOver($event, attr.originalIndex)"
                        @dragleave="handleDragLeave"
                        @drop.prevent="handleDrop($event, attr.originalIndex)"
                      >
                        <div class="drag-handle" v-if="isEditMode" title="拖拽排序">
                          <el-icon><Rank /></el-icon>
                        </div>
                        <div class="attribute-label">字段：</div>
                        <el-input
                          :model-value="attr.title"
                          placeholder="请输入标题"
                          :readonly="!isEditMode"
                          @update:model-value="(value: string) => updateSpecialAttribute(attr.originalIndex, value)"
                          class="attribute-input"
                          :style="{ width: isEditMode ? '130px' : (attr.options.length > 0 ? '110px' : '170px') }"
                        />
                        <div v-if="!isEditMode && attr.options.length > 0" class="attribute-options-display">
                          <span class="options-label">选项：</span>
                          <el-tag
                            v-for="(opt, optIndex) in attr.options"
                            :key="optIndex"
                            size="small"
                            type="info"
                            class="option-tag"
                          >
                            {{ opt }}
                          </el-tag>
                        </div>
                        <el-button
                          v-if="isEditMode"
                          type="primary"
                          size="small"
                          @click="openOptionsDialog(attr.originalIndex)"
                          class="attribute-options-btn"
                        >
                          选项{{ attr.options.length > 0 ? `(${attr.options.length})` : '' }}
                        </el-button>
                        <el-button
                          v-if="isEditMode"
                          type="danger"
                          size="small"
                          @click="deleteSpecialAttribute(attr.originalIndex)"
                          class="attribute-delete-btn"
                        >
                          <el-icon><Delete /></el-icon>
                        </el-button>
                        <el-tooltip v-if="isEditMode" content="切换为全局属性" placement="top">
                          <el-button
                            type="info"
                            size="small"
                            link
                            class="attribute-switch-btn"
                            @click="toggleAttributeScope(attr.originalIndex)"
                          >
                            ←全局
                          </el-button>
                        </el-tooltip>
                      </div>
                    </div>
                    <el-button
                      v-if="isEditMode"
                      type="primary"
                      plain
                      size="small"
                      @click="addSpecialAttribute(true)"
                      class="add-attribute-btn"
                    >
                      <el-icon><Plus /></el-icon> 添加单条字段
                    </el-button>
                  </div>
                </div>
              </div>

              <!-- 图片上传区域 -->
              <div class="image-upload-section">
                 <div class="attributes-title" style="font-size: 14px; font-weight: 600; color: #606266; margin-bottom: 10px;">示意图上传</div>
                 
                 <!-- 上传进度显示 -->
                 <div v-if="imageUploading" class="upload-progress-container">
                   <el-progress 
                     type="circle" 
                     :percentage="uploadProgress" 
                     :width="80"
                     :stroke-width="6"
                   >
                     <template #default="{ percentage }">
                       <span class="progress-text">{{ percentage }}%</span>
                     </template>
                   </el-progress>
                   <div class="progress-label">上传中...</div>
                   
                 </div>
                 
                 <el-upload
                   v-if="imageFileList.length === 0 && isEditMode && !imageUploading"
                   ref="imageUploadRef"
                   class="image-upload"
                   drag
                   action="#"
                   :http-request="handleImageUpload"
                   :file-list="imageFileList"
                   :limit="1"
                   :on-exceed="handleExceed"
                   :on-remove="handleImageRemove"
                   :on-preview="previewImage"
                   :disabled="!isEditMode || imageUploading"
                   :show-file-list="false"
                 >
                   <el-icon class="el-icon--upload"><upload-filled /></el-icon>
                   <div class="el-upload__text">
                     拖拽文件到此处或 <em>点击上传</em>
                   </div>
                 </el-upload>
                 
                 <!-- 自定义已上传文件显示 -->
                 <div v-if="imageFileList.length > 0" class="uploaded-file-item" style="margin-top: 10px; display: flex; align-items: center; justify-content: space-between; padding: 10px; background: #f5f7fa; border-radius: 4px; border: 1px solid #e4e7ed;">
                   <div style="display: flex; align-items: center; cursor: pointer; overflow: hidden; flex: 1;" @click="previewImage(imageFileList[0])">
                     <div class="file-thumbnail-wrapper" v-loading="(imageFileList[0] as any).loading" style="position: relative; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; margin-right: 8px; border: 1px solid #e4e7ed; border-radius: 4px; overflow: hidden; background-color: #f5f7fa;">
                       <img v-if="(imageFileList[0] as any).url" :src="(imageFileList[0] as any).url" style="width: 100%; height: 100%; object-fit: cover;" alt="缩略图" />
                       <el-icon v-else style="font-size: 20px; color: #909399;"><Document /></el-icon>
                     </div>
                     <span style="font-size: 14px; margin-right: 10px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 150px;" :title="imageFileList[0].name">{{ imageFileList[0].name }}</span>
                     <el-tag size="small" type="success">已上传</el-tag>
                   </div>
                   <el-button 
                     type="danger" 
                     circle 
                     size="small" 
                     @click="handleImageRemove"
                     :disabled="!isEditMode"
                   >
                     <el-icon><Delete /></el-icon>
                   </el-button>
                 </div>
              </div>
            </div>

            <div class="axis-placeholder-tip">@@*表示占位符，系统自动取对应位置的轴线号</div>
            
            <div class="toolbar" v-if="isEditMode">
              <div class="toolbar-left" v-if="!currentTable.type || currentTable.type === '表格'">
                <el-button
                  type="success"
                  @click="addColumn"
                  size="default"
                >
                  <el-icon><Plus /></el-icon> 增加列
                </el-button>
                <el-button
                  type="primary"
                  @click="addRow"
                  size="default"
                >
                  <el-icon><Plus /></el-icon> 增加行
                </el-button>
                <span class="toolbar-tip">提示：左键点击表头黑线可设置分界线，右键点击可插入新列</span>
              </div>
              <div class="toolbar-right">
                <el-button v-if="can('保存')" type="primary" @click="saveTableData">保存数据</el-button>
                <el-button @click="clearTable" v-if="!currentTable.type || currentTable.type === '表格'">清空表格</el-button>
              </div>
            </div>
            
            <div class="table-container" v-if="!currentTable.type || currentTable.type === '表格'">
              <!-- 表格包装器（用于定位分区名称） -->
              <div class="table-wrapper">
                <!-- 分区名称浮动层（独立于表格，避免层叠上下文问题） -->
                <div class="section-names-overlay" :key="`overlay-${sectionNameKey}`">
                  <!-- 第一分区名称（从第一列开始） -->
                  <div
                    v-if="currentTable.sectionNames[-1]"
                    class="section-name-label"
                    :style="getFirstSectionNameStyle()"
                    :title="currentTable.sectionNames[-1]"
                  >
                    分区名称：{{ currentTable.sectionNames[-1] }}
                  </div>
                  <!-- 其他分区名称（分界线右侧） -->
                  <template v-for="(header, colIndex) in currentTable.horizontalHeaders" :key="`section-${colIndex}-${sectionNameKey}`">
                    <div
                      v-if="currentTable.columnBorders[colIndex] && currentTable.sectionNames[colIndex]"
                      class="section-name-label"
                      :style="getSectionNameStyle(colIndex)"
                      :title="currentTable.sectionNames[colIndex]"
                    >
                      分区名称：{{ currentTable.sectionNames[colIndex] }}
                    </div>
                  </template>
                </div>
                <table class="editable-table" ref="editableTableRef">
                  <thead>
                    <tr>
                      <!-- 左上角标题 -->
                      <th class="corner-header" ref="cornerHeaderRef">
                        标题
                        <!-- 第一列分区名称设置按钮（只设置分区名称，不设置分界线） - 已禁用 -->
                        <div
                          v-if="false && isEditMode && currentTable.horizontalHeaders.length > 0"
                          @click="setFirstSectionName"
                          @contextmenu.prevent="handleColumnBorderRightClick($event, -1)"
                          :class="['column-border-btn', 'first-column-border', { 'active': currentTable.sectionNames[-1] }]"
                          title="左键：设置第一分区名称 | 右键：插入新列"
                        ></div>
                    </th>
                    <!-- 横向标题行 -->
                    <th
                      v-for="(header, colIndex) in currentTable.horizontalHeaders"
                      :key="`header-${colIndex}`"
                      :ref="(el) => setColumnHeaderRef(el as HTMLElement, colIndex)"
                      :class="['table-header', { 'has-border': currentTable.columnBorders[colIndex] }]"
                    >
                      <el-input
                        :model-value="header"
                        placeholder="横向标题"
                        :readonly="!isEditMode"
                        @update:model-value="(value: string) => updateColumnHeader(colIndex, value)"
                        class="header-input"
                      />
                      <el-button
                        v-if="isEditMode && ['select', 'preset'].includes(currentTable.columnTypes[colIndex])"
                        type="primary"
                        size="small"
                        @click="openSelectOptionsDialog(colIndex)"
                        class="select-options-btn"
                        :title="currentTable.columnTypes[colIndex] === 'preset' ? '编辑表格预设值' : (colIndex === 0 ? '编辑下拉选项，可绑定图片' : '编辑下拉选项（仅文字）')"
                      >
                        <el-icon><Edit /></el-icon>
                      </el-button>
                      <el-button
                        v-if="isEditMode"
                        type="info"
                        size="small"
                        @click="openColumnTypeDialog(colIndex)"
                        class="column-type-btn"
                        title="设置列类型"
                      >
                        <el-icon><Setting /></el-icon>
                      </el-button>
                      <el-button
                        v-if="isEditMode"
                        type="danger"
                        size="small"
                        @click="deleteColumn(colIndex)"
                        class="delete-column-btn"
                      >
                        <el-icon><Delete /></el-icon>
                      </el-button>
                      <!-- 列边界线设置按钮（绝对定位在列右边） - 只设置分界线，不设置分区名称 -->
                      <div
                        v-if="isEditMode && colIndex < currentTable.horizontalHeaders.length - 1"
                        @click="toggleColumnBorderOnly(colIndex)"
                        @contextmenu.prevent="handleColumnBorderRightClick($event, colIndex)"
                        :class="['column-border-btn', { 'active': currentTable.columnBorders[colIndex] }]"
                        title="左键：设置分界线 | 右键：插入新列"
                      ></div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(verticalHeader, rowIndex) in currentTable.verticalHeaders"
                    :key="`row-${rowIndex}`"
                  >
                    <!-- 竖向标题列 -->
                    <td class="vertical-header-cell">
                      <el-input
                        :model-value="verticalHeader"
                        placeholder="竖向标题"
                        :readonly="!isEditMode"
                        @update:model-value="(value: string) => updateVerticalHeader(rowIndex, value)"
                        class="vertical-header-input"
                      />
                    </td>
                    <!-- 数据单元格 -->
                    <td
                      v-for="(cell, colIndex) in currentTable.data[rowIndex]"
                      :key="`cell-${rowIndex}-${colIndex}`"
                      :class="[
                        'data-cell',
                        {
                          'has-border': currentTable.columnBorders[colIndex],
                          'auto-fill-group-start': isAutoFillGroupStart(rowIndex, colIndex)
                        }
                      ]"
                    >
                      <!-- 文本输入框 -->
                      <el-input
                        v-if="currentTable.columnTypes[colIndex] === 'text'"
                        :model-value="cell"
                        placeholder=""
                        readonly
                      />
                      <!-- 数字输入框 -->
                      <el-input-number
                        v-else-if="currentTable.columnTypes[colIndex] === 'number'"
                        :model-value="Number(cell) || 0"
                        placeholder=""
                        readonly
                        controls-position="right"
                        style="width: 100%"
                      />
                      <!-- 单选框（复选框样式） -->
                      <el-checkbox
                        v-else-if="currentTable.columnTypes[colIndex] === 'radio'"
                        :model-value="cell === '是' || cell === 'true' || cell === '1'"
                        readonly
                        class="cell-checkbox"
                      />
                      <!-- 表格预设值列 -->
                      <el-input
                        v-else-if="currentTable.columnTypes[colIndex] === 'preset'"
                        :model-value="cell"
                        readonly
                        class="preset-value-input"
                      />
                      <!-- 下拉选择框 -->
                      <el-select
                        v-else-if="currentTable.columnTypes[colIndex] === 'select'"
                        :model-value="cell"
                        placeholder=""
                        readonly
                        style="width: 100%"
                      >
                        <el-option
                          v-for="(option, optionIndex) in (currentTable.selectOptions[colIndex] || [])"
                          :key="optionIndex"
                          :label="getSelectOptionText(option)"
                          :value="option"
                        />
                      </el-select>
                    </td>
                    <!-- 删除行按钮 -->
                    <td class="row-control" v-if="isEditMode">
                      <el-button
                        type="danger"
                        size="small"
                        @click="deleteRow(rowIndex)"
                        class="delete-row-btn"
                      >
                        <el-icon><Delete /></el-icon>
                      </el-button>
                    </td>
                    <td v-else class="row-control"></td>
                  </tr>
                </tbody>
              </table>
              </div>
            </div>
          </div>
          
          <div v-else class="empty-state">
            <el-empty description="请先创建或选择一个表格" />
          </div>
        </div>
      </div>
    </el-card>
    
    <!-- 列类型设置对话框 -->
    <el-dialog
      v-model="showColumnTypeDialog"
      title="设置列类型"
      width="760px"
      @close="cancelColumnType"
    >
      <div class="column-type-options">
        <div
          v-for="option in columnTypeOptions"
          :key="option.value"
          :class="['column-type-item', { 'active': currentColumnType === option.value }]"
          @click="confirmColumnType(option.value as ColumnType)"
        >
          <div class="type-icon">
            <el-icon v-if="option.value === 'text'"><Edit /></el-icon>
            <el-icon v-else-if="option.value === 'number'"><DataAnalysis /></el-icon>
            <el-icon v-else-if="option.value === 'radio'"><CircleCheck /></el-icon>
            <el-icon v-else-if="option.value === 'select'"><ArrowDown /></el-icon>
            <el-icon v-else-if="option.value === 'preset'"><CollectionTag /></el-icon>
          </div>
          <div class="type-label">{{ option.label }}</div>
          <el-icon v-if="currentColumnType === option.value" class="check-icon">
            <Check />
          </el-icon>
        </div>
      </div>
      <div class="auto-fill-row-setting">
        <span class="auto-fill-row-label">自动填充行数</span>
        <el-select
          v-model="editingAutoFillRows"
          size="small"
          @change="updateEditingAutoFillRows"
        >
          <el-option label="不自动填充" :value="0" />
          <el-option
            v-for="count in autoFillRowOptions"
            :key="count"
            :label="`${count} 行一组`"
            :value="count"
          />
        </el-select>
        <span class="auto-fill-row-help">例如选择 3，表示每 3 行为一组，并显示横向分界线。</span>
      </div>
      <template #footer>
        <el-button @click="cancelColumnType">取消</el-button>
      </template>
    </el-dialog>
    
    <!-- 编辑下拉选项对话框 -->
    <el-dialog
      v-model="showSelectOptionsDialog"
      :title="selectOptionsDialogTitle"
      width="760px"
      @close="cancelSelectOptions"
    >
      <div class="select-options-editor">
        <div v-if="isPresetValueColumn" class="preset-value-tip">
          当前表格共 {{ currentTable?.verticalHeaders.length || 0 }} 行，请为每一行设置对应的预设值。
          自动填充行数是独立的列配置，不影响这里的预设值数量。
        </div>
        <div class="options-list">
          <div
            v-for="(option, index) in editingSelectOptions"
            :key="index"
            class="option-item"
          >
            <div class="option-main">
              <el-input
                v-model="editingSelectOptions[index]"
                :placeholder="isPresetValueColumn ? `第 ${index + 1} 组预设值` : `选项 ${index + 1}`"
                @blur="validateOption(index)"
              />
              <div class="option-image-preview" v-if="!isPresetValueColumn && canUploadSelectOptionImage && getSelectOptionImageCode(option)">
                <div
                  class="option-thumb"
                  v-loading="selectOptionImageMeta[index]?.loading"
                  @click="previewSelectOptionImage(index)"
                >
                  <img
                    v-if="selectOptionImageMeta[index]?.thumbnailUrl"
                    :src="selectOptionImageMeta[index].thumbnailUrl"
                    alt="选项图片"
                  />
                  <el-icon v-else><Document /></el-icon>
                </div>
                <div class="option-image-info">
                  <span class="option-image-name" :title="getSelectOptionImageCode(option)">
                    {{ getSelectOptionImageCode(option) }}
                  </span>
                  <el-tag size="small" type="success">已绑定</el-tag>
                </div>
              </div>
            </div>
            <el-upload
              v-if="!isPresetValueColumn && canUploadSelectOptionImage"
              class="option-upload"
              action="#"
              accept="image/*"
              :show-file-list="false"
              :http-request="(uploadOptions: any) => handleSelectOptionImageUpload(uploadOptions, index)"
              :disabled="selectOptionImageMeta[index]?.uploading"
            >
              <el-button
                type="primary"
                size="small"
                plain
                :loading="selectOptionImageMeta[index]?.uploading"
              >
                上传图片
              </el-button>
            </el-upload>
            <el-button
              v-if="!isPresetValueColumn && canUploadSelectOptionImage && getSelectOptionImageCode(option)"
              type="warning"
              size="small"
              plain
              @click="clearSelectOptionImage(index)"
            >
              解绑
            </el-button>
            <el-button
              v-if="!isPresetValueColumn"
              type="danger"
              size="small"
              @click="removeSelectOption(index)"
              :readonly="editingSelectOptions.length <= 1"
            >
              <el-icon><Delete /></el-icon>
            </el-button>
          </div>
        </div>
        <el-button
          v-if="!isPresetValueColumn"
          type="primary"
          @click="addSelectOption"
          class="add-option-btn"
        >
          <el-icon><Plus /></el-icon> 添加选项
        </el-button>
      </div>
      <template #footer>
        <el-button @click="cancelSelectOptions">取消</el-button>
        <el-button type="primary" @click="confirmSelectOptions">确定</el-button>
      </template>
    </el-dialog>

    <el-dialog
      v-model="selectOptionImagePreviewVisible"
      title="选项图片预览"
      width="72%"
      top="6vh"
      append-to-body
    >
      <div class="select-option-image-preview-dialog">
        <img v-if="selectOptionPreviewUrl" :src="selectOptionPreviewUrl" alt="选项图片预览" />
        <div v-else class="preview-loading">图片加载中...</div>
      </div>
    </el-dialog>

    <!-- 特殊属性选项编辑弹框 -->
    <el-dialog
      v-model="optionsDialogVisible"
      title="编辑特殊属性选项"
      width="400px"
      :close-on-click-modal="false"
    >
      <div>
        <div 
          v-for="(opt, index) in currentOptions" 
          :key="index" 
          style="display: flex; align-items: center; gap: 8px; margin-bottom: 10px;"
        >
          <el-input
            v-model="currentOptions[index]"
            placeholder="请输入选项"
            style="flex: 1;"
          />
          <el-button
            type="danger"
            size="small"
            @click="deleteOption(index)"
          >
            <el-icon><Delete /></el-icon>
          </el-button>
        </div>
        <el-button
          type="primary"
          size="small"
          @click="addOption"
          style="margin-top: 10px;"
        >
          <el-icon><Plus /></el-icon> 添加选项
        </el-button>
      </div>
      <template #footer>
        <el-button @click="optionsDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveOptions">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted, onActivated } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { Plus, Delete, Edit, Close, Setting, UploadFilled, Document, Refresh, DataAnalysis, CircleCheck, ArrowDown, Check, Printer, Rank, CollectionTag, Collection, DocumentCopy, CopyDocument } from '@element-plus/icons-vue';
import { ElMessage, ElMessageBox, UploadProps, UploadUserFile, genFileId, UploadInstance } from 'element-plus';
import DynamicTable from '@/components/DynamicTable.vue';
import { ParameterSettings } from '@/network/index';
import type { ColumnType, TableData } from './types';
import { parseTableDetail, formatTableDataForSave, createDefaultTableData } from './utils';
import { usePageStateStore } from '@/store/pageStateStore';
import { useDetailStore } from '@/store/detailStore';
import { getMenuChildPathByComponent } from '@/utils/menuRoute';
import { useButtonPermission } from '@/utils/buttonPermission';

const { can } = useButtonPermission('/ParameterSettings/tableEditing/index.vue');

const router = useRouter();
const route = useRoute();
const pageStateStore = usePageStateStore();
const detailStore = useDetailStore();
const activeTableIndex = ref<number>(-1);
// tableList 存储接口返回的原始数据（用于左侧列表展示）
const tableList = ref<any[]>([]);
// model数据，用于表格表头展示
const tableModel = ref<Record<string, string> | undefined>(undefined);
// currentTable 存储当前编辑的表格数据（完整的 TableData 结构）
const currentTable = ref<TableData | null>(null);
const showNameError = ref(false);
const showControlNumberError = ref(false);
const showNumberSuffixError = ref(false);
const showImplementDateError = ref(false);
const suppressTableListAutoSelect = ref(false);
const deviceTypeLoading = ref(false);
const deviceTypeOptions = ref<Array<{ 名称: string; 提示词?: string; Id: string | number }>>([]);
const DEFAULT_DEVICE_TYPE = '单个数据读数设备';

const getDefaultDeviceType = () => {
  const matched = deviceTypeOptions.value.find(item => item.名称 === DEFAULT_DEVICE_TYPE);
  return matched?.名称 || DEFAULT_DEVICE_TYPE;
};

// 左侧面板拖拽调整宽度相关
const mainContentRef = ref<HTMLElement | null>(null);
const leftPanelRef = ref<HTMLElement | null>(null);
const leftPanelWidth = ref<number>(300);
const isResizing = ref<boolean>(false);
const minPanelWidth = 200;

// 表格容器和列头引用（用于计算分区名称位置）
const editableTableRef = ref<HTMLTableElement | null>(null);
const cornerHeaderRef = ref<HTMLElement | null>(null);
const columnHeaderRefs = ref<Map<number, HTMLElement>>(new Map());

// 设置列头引用
const setColumnHeaderRef = (el: HTMLElement | null, colIndex: number) => {
  if (el) {
    columnHeaderRefs.value.set(colIndex, el);
  } else {
    columnHeaderRefs.value.delete(colIndex);
  }
};

// 计算分区名称的位置样式（相对于 table-wrapper）
// columnBorders[colIndex] 表示第 colIndex 列与第 colIndex+1 列之间的分界线
// sectionNames[colIndex] 是分界线右侧的分区名称
const getSectionNameStyle = (colIndex: number) => {
  const tableEl = editableTableRef.value;
  if (!tableEl) {
    return { display: 'none' };
  }

  const tableRect = tableEl.getBoundingClientRect();
  
  // 分区名称显示在第 colIndex 列的右边界
  const headerEl = columnHeaderRefs.value.get(colIndex);
  if (!headerEl) {
    return { display: 'none' };
  }
  const headerRect = headerEl.getBoundingClientRect();
  const left = headerRect.right - tableRect.left + 8;

  return {
    left: `${left}px`,
    transform: 'translateX(0)'
  };
};

// 计算第一分区名称的位置样式（显示在标题列右边界，即第一列左边）
const getFirstSectionNameStyle = () => {
  const tableEl = editableTableRef.value;
  const cornerEl = cornerHeaderRef.value;
  if (!tableEl || !cornerEl) {
    return { display: 'none' };
  }

  const tableRect = tableEl.getBoundingClientRect();
  const cornerRect = cornerEl.getBoundingClientRect();
  const left = cornerRect.right - tableRect.left + 8;

  return {
    left: `${left}px`,
    transform: 'translateX(0)'
  };
};

// 分区名称位置更新触发器
const sectionNameKey = ref(0);

// 强制更新分区名称位置
const updateSectionNames = () => {
  sectionNameKey.value++;
};

// 监听 currentTable 变化，延迟更新分区名称位置
watch(
  () => currentTable.value,
  () => {
    // 等待 DOM 更新后重新计算位置
    nextTick(() => {
      setTimeout(updateSectionNames, 100);
    });
  },
  { deep: true }
);

watch(() => currentTable.value, () => {
  showNameError.value = false;
  showControlNumberError.value = false;
  showNumberSuffixError.value = false;
  showImplementDateError.value = false;
});

const startResize = (e: MouseEvent) => {
  isResizing.value = true;
  document.addEventListener('mousemove', handleResize);
  document.addEventListener('mouseup', stopResize);
  document.body.style.cursor = 'col-resize';
  document.body.style.userSelect = 'none';
};

const handleResize = (e: MouseEvent) => {
  if (!isResizing.value || !mainContentRef.value) return;
  const containerRect = mainContentRef.value.getBoundingClientRect();
  const maxWidth = containerRect.width / 2;
  let newWidth = e.clientX - containerRect.left;
  newWidth = Math.max(minPanelWidth, Math.min(maxWidth, newWidth));
  leftPanelWidth.value = newWidth;
};

const stopResize = () => {
  isResizing.value = false;
  document.removeEventListener('mousemove', handleResize);
  document.removeEventListener('mouseup', stopResize);
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
};

onUnmounted(() => {
  document.removeEventListener('mousemove', handleResize);
  document.removeEventListener('mouseup', stopResize);
});

// 加载状态
const loading = ref<boolean>(false); // 右侧详情区域加载状态
const listLoading = ref<boolean>(false); // 左侧列表加载状态

// 筛选参数
const filterParams = ref<any[]>([]);

// 分页配置
const pagination = ref({
  show: true,
  page: 1,
  pageSize: 10,
  total: 0,
  pageSizes: [10, 20, 50, 100],

  layout: 'total, sizes, prev, pager, next',
  small: true
});

// 编辑模式控制
const isEditMode = ref<boolean>(false);

// 列类型设置相关
const showColumnTypeDialog = ref(false);
const editingColumnIndex = ref<number>(-1);
const editingAutoFillRows = ref(0);
const columnTypeOptions = [
  { label: '文本输入框', value: 'text' },
  { label: '数字输入框', value: 'number' },
  { label: '单选框', value: 'radio' },
  { label: '下拉选择框', value: 'select' },
  { label: '表格预设值列', value: 'preset' }
] as const;

// 辅助函数：创建默认表格数据
const createEmptyTable = (name?: string): TableData => createDefaultTableData(name);

const clearRequiredFieldErrors = () => {
  showNameError.value = false;
  showControlNumberError.value = false;
  showNumberSuffixError.value = false;
  showImplementDateError.value = false;
};

const validateRequiredFields = () => {
  if (!currentTable.value) return false;
  showNameError.value = !String(currentTable.value.name || '').trim();
  showControlNumberError.value = !String(currentTable.value.controlNumber || '').trim();
  showNumberSuffixError.value = !String(currentTable.value.numberSuffix || '').trim();
  showImplementDateError.value = !currentTable.value.implementDate;
  return !showNameError.value
    && !showControlNumberError.value
    && !showNumberSuffixError.value
    && !showImplementDateError.value;
};

// 辅助函数：查找表格列表中的项索引
const findTableIndex = (row: any): number => {
  return tableList.value.findIndex((item: any) => {
    if (row.Id !== undefined && item.Id !== undefined) return item.Id === row.Id;
    if (row.id !== undefined && item.id !== undefined) return item.id === row.id;
    return item === row;
  });
};

// 辅助函数：查找表格列表中的项（优先通过ID匹配，ID不存在时才用名称匹配）
const findTableItem = (targetId?: number | string, targetName?: string): any => {
  // 优先通过ID精确匹配
  if (targetId) {
    const targetIdText = String(targetId);
    const foundById = tableList.value.find((item: any) => 
      String(item.Id ?? item.id ?? '') === targetIdText
    );
    if (foundById) return foundById;
  }
  // ID匹配失败时，才通过名称匹配
  if (targetName) {
    return tableList.value.find((item: any) => item.名称 === targetName || item.name === targetName);
  }
  return null;
};

// 下拉选项编辑相关
const showSelectOptionsDialog = ref(false);
const editingSelectOptionsColumnIndex = ref<number>(-1);
const editingSelectOptions = ref<string[]>([]);
const selectOptionImageMeta = ref<Record<number, {
  uploading?: boolean;
  loading?: boolean;
  thumbnailUrl?: string;
}>>({});
const selectOptionImagePreviewVisible = ref(false);
const selectOptionPreviewUrl = ref('');
const SELECT_OPTION_IMAGE_SEPARATOR = '|||';
const isPresetValueColumn = computed(() => {
  const columnIndex = editingSelectOptionsColumnIndex.value;
  return columnIndex >= 0 && currentTable.value?.columnTypes[columnIndex] === 'preset';
});
const canUploadSelectOptionImage = computed(() => (
  !isPresetValueColumn.value && editingSelectOptionsColumnIndex.value === 0
));
const selectOptionsDialogTitle = computed(() => (
  isPresetValueColumn.value
    ? '编辑表格预设值'
    : (canUploadSelectOptionImage.value ? '编辑下拉选项（第一列可绑定图片）' : '编辑下拉选项（仅文字）')
));

const autoFillRowOptions = computed(() => {
  const rowCount = currentTable.value?.verticalHeaders.length || 1;
  return Array.from({ length: rowCount }, (_, index) => index + 1);
});

const syncPresetColumn = (colIndex: number) => {
  if (!currentTable.value || currentTable.value.columnTypes[colIndex] !== 'preset') return;

  const rowCount = currentTable.value.verticalHeaders.length;
  const previousValues = currentTable.value.selectOptions[colIndex] || [];
  const values = Array.from({ length: rowCount }, (_, rowIndex) => (
    String(previousValues[rowIndex] || '')
  ));

  currentTable.value.selectOptions[colIndex] = values;
  currentTable.value.data.forEach((row, rowIndex) => {
    row[colIndex] = values[rowIndex] || '';
  });
};

const updateEditingAutoFillRows = (value: string | number) => {
  if (!currentTable.value || editingColumnIndex.value < 0) return;
  if (!currentTable.value.autoFillRowCounts) currentTable.value.autoFillRowCounts = [];
  currentTable.value.autoFillRowCounts[editingColumnIndex.value] = Math.max(0, Number(value) || 0);
};

const isAutoFillGroupStart = (rowIndex: number, colIndex: number) => {
  if (!currentTable.value || rowIndex <= 0) return false;
  const fillRows = Number(currentTable.value.autoFillRowCounts[colIndex]) || 0;
  return fillRows > 1 && rowIndex % fillRows === 0;
};

// 图片上传相关
const imageUploadRef = ref<UploadInstance>();
const imageFileList = ref<UploadUserFile[]>([]);
const imageUploading = ref<boolean>(false); // 图片上传中状态
const uploadProgress = ref<number>(0); // 上传进度百分比
// 添加用于存储缩略图URL的Map
const thumbnailCache = new Map<string, string>(); // key: serverCode, value: thumbnail URL

const splitSelectOptionValue = (value: string) => {
  const [text = '', imageCode = ''] = String(value || '').split(SELECT_OPTION_IMAGE_SEPARATOR);
  return {
    text: text.trim(),
    imageCode: imageCode.trim()
  };
};

const buildSelectOptionValue = (text: string, imageCode?: string) => {
  const optionText = text.trim();
  const optionImageCode = String(imageCode || '').trim();
  return optionImageCode ? `${optionText}${SELECT_OPTION_IMAGE_SEPARATOR}${optionImageCode}` : optionText;
};

const getSelectOptionText = (value: string) => splitSelectOptionValue(value).text;

const getSelectOptionImageCode = (value: string) => splitSelectOptionValue(value).imageCode;

const stripSelectOptionImages = (options: string[]) => {
  return options.map((option, index) => getSelectOptionText(option) || `选项${index + 1}`);
};

const normalizeSelectOptionImages = (table: TableData) => {
  table.selectOptions = table.selectOptions.map((options, colIndex) => (
    colIndex === 0 ? options : stripSelectOptionImages(options || [])
  ));
};

// 获取图片内容（用于缩略图和预览）
const fetchImageContent = async (serverCode: string): Promise<string | null> => {
  if (!serverCode) return null;
  
  // Check cache first
  if (thumbnailCache.has(serverCode)) {
    return thumbnailCache.get(serverCode)!;
  }

  try {
    const res: any = await ParameterSettings.parameterSettingsDownload({ fileName: serverCode });
    let imageUrl = '';
    
    if (res instanceof Blob) {
      imageUrl = URL.createObjectURL(res);
    } else {
      // 尝试从不同结构中获取数据
      const data = res?.response || res?.data || res;
      
      if (data instanceof Blob) {
        imageUrl = URL.createObjectURL(data);
      } else if (typeof data === 'string') {
        if (data.startsWith('http') || data.startsWith('blob:') || data.startsWith('data:')) {
          imageUrl = data;
        } else {
          imageUrl = `data:image/jpeg;base64,${data}`;
        }
      }
    }

    if (imageUrl) {
      thumbnailCache.set(serverCode, imageUrl);
      return imageUrl;
    }
  } catch (e) {
    console.warn(`图片加载失败 (${serverCode}):`, e);
  }
  return null;
};

const setSelectOptionImageMeta = (index: number, meta: Partial<{
  uploading: boolean;
  loading: boolean;
  thumbnailUrl: string;
}>) => {
  selectOptionImageMeta.value = {
    ...selectOptionImageMeta.value,
    [index]: {
      ...(selectOptionImageMeta.value[index] || {}),
      ...meta
    }
  };
};

const loadSelectOptionThumbnail = async (index: number) => {
  const imageCode = getSelectOptionImageCode(editingSelectOptions.value[index] || '');
  if (!imageCode) {
    setSelectOptionImageMeta(index, { loading: false, thumbnailUrl: '' });
    return;
  }

  setSelectOptionImageMeta(index, { loading: true });
  try {
    const url = await fetchImageContent(imageCode);
    setSelectOptionImageMeta(index, { loading: false, thumbnailUrl: url || '' });
  } catch (error) {
    console.warn('加载选项图片缩略图失败:', error);
    setSelectOptionImageMeta(index, { loading: false, thumbnailUrl: '' });
  }
};

const refreshSelectOptionThumbnails = () => {
  selectOptionImageMeta.value = {};
  editingSelectOptions.value.forEach((option, index) => {
    if (getSelectOptionImageCode(option)) {
      loadSelectOptionThumbnail(index);
    }
  });
};

const handleSelectOptionImageUpload = async (options: any, index: number) => {
  if (!canUploadSelectOptionImage.value) {
    ElMessage.warning('仅第一列选项可上传图片');
    return;
  }
  const file = options.file;
  if (!file) return;

  setSelectOptionImageMeta(index, { uploading: true });
  try {
    const form = new FormData();
    form.append('image', file, file.name || 'upload.jpg');

    const res = await ParameterSettings.parameterSettingsUploadWithProgress(form);
    if (res && res.success) {
      const imageCode = String(res.response || res.data || file.name || '').trim();
      if (!imageCode) {
        ElMessage.error('上传成功但未返回图片名称');
        return;
      }

      const currentText = getSelectOptionText(editingSelectOptions.value[index] || '') || `选项${index + 1}`;
      editingSelectOptions.value[index] = buildSelectOptionValue(currentText, imageCode);
      await loadSelectOptionThumbnail(index);
      ElMessage.success('选项图片绑定成功');
    } else {
      ElMessage.error('选项图片上传失败');
    }
  } catch (error) {
    console.error('选项图片上传失败:', error);
    ElMessage.error('选项图片上传失败');
  } finally {
    setSelectOptionImageMeta(index, { uploading: false });
  }
};

const clearSelectOptionImage = (index: number) => {
  if (!canUploadSelectOptionImage.value) return;
  const optionText = getSelectOptionText(editingSelectOptions.value[index] || '');
  editingSelectOptions.value[index] = optionText;
  setSelectOptionImageMeta(index, { loading: false, uploading: false, thumbnailUrl: '' });
};

const previewSelectOptionImage = async (index: number) => {
  if (!canUploadSelectOptionImage.value) return;
  const imageCode = getSelectOptionImageCode(editingSelectOptions.value[index] || '');
  if (!imageCode) return;

  const cachedUrl = selectOptionImageMeta.value[index]?.thumbnailUrl;
  if (cachedUrl) {
    selectOptionPreviewUrl.value = cachedUrl;
    selectOptionImagePreviewVisible.value = true;
    return;
  }

  selectOptionPreviewUrl.value = '';
  selectOptionImagePreviewVisible.value = true;
  const url = await fetchImageContent(imageCode);
  if (url) {
    selectOptionPreviewUrl.value = url;
    setSelectOptionImageMeta(index, { thumbnailUrl: url });
  } else {
    selectOptionImagePreviewVisible.value = false;
    ElMessage.error('选项图片预览失败');
  }
};

// 监听图片代码变化
watch(() => currentTable.value?.imageCode, async (newCode) => {
  if (newCode) {
    const fileObj: any = {
      name: currentTable.value?.imageName || '示意图.jpg',
      url: '', 
      uid: -1,
      status: 'success',
      serverCode: newCode,
      loading: true
    };
    imageFileList.value = [fileObj];
    
    // 异步获取图片用于缩略图
    fetchImageContent(newCode).then(url => {
      if (url) {
        fileObj.url = url;
      }
      fileObj.loading = false;
      // 强制触发响应式更新
      imageFileList.value = [{ ...fileObj }];
    }).catch(() => {
      fileObj.loading = false;
      imageFileList.value = [{ ...fileObj }];
    });
  } else {
    imageFileList.value = [];
  }
}, { immediate: true });

const handleImageUpload = async (options: any) => {
  imageUploading.value = true; // 开始上传
  uploadProgress.value = 0; // 重置进度
  try {
    const form = new FormData();
    form.append('image', options.file);
    
    // 使用带进度监听的上传
    const res = await ParameterSettings.parameterSettingsUploadWithProgress(form, (progress: number) => {
      uploadProgress.value = progress;
    });
    
    if (res && res.success) {
        const code = res.response || res.data;
        if (currentTable.value) {
            currentTable.value.imageCode = code;
            currentTable.value.imageName = options.file.name;
        }
        
        const fileObj: any = {
            name: options.file.name,
            uid: options.file.uid,
            status: 'success',
            serverCode: code,
            url: '',
            loading: true
        };
        imageFileList.value = [fileObj];
        
        // 立即获取图片用于缩略图
        fetchImageContent(code).then(url => {
          if (url) {
            fileObj.url = url;
          }
          fileObj.loading = false;
          // 强制触发响应式更新
          imageFileList.value = [{ ...fileObj }];
        }).catch(() => {
          fileObj.loading = false;
          imageFileList.value = [{ ...fileObj }];
        });
        
        ElMessage.success('上传成功');
    } else {
        ElMessage.error('上传失败');
        imageFileList.value = [];
    }
  } catch (e) {
    console.error(e);
    ElMessage.error('上传失败');
    imageFileList.value = [];
  } finally {
    imageUploading.value = false; // 上传结束
    uploadProgress.value = 0; // 重置进度
  }
};

const handleImageRemove = async () => {
  if (currentTable.value?.imageCode) {
    try {
      await ElMessageBox.confirm('确定要删除这张图片吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      });

      const res = await ParameterSettings.parameterSettingsDeleteImage({ 
        fileName: currentTable.value.imageCode 
      });

      if (res && res.success) {
        if (currentTable.value) {
          currentTable.value.imageCode = '';
          currentTable.value.imageName = '';
        }
        imageFileList.value = [];
        ElMessage.success('图片删除成功');
      } else {
        ElMessage.error(res?.msg || '图片删除失败');
      }
    } catch (error) {
      if (error !== 'cancel') {
        console.error(error);
        ElMessage.error('图片删除失败');
      }
    }
  } else {
    // 如果没有服务器端代码（仅本地状态），直接清除
    if (currentTable.value) {
      currentTable.value.imageCode = '';
      currentTable.value.imageName = '';
    }
    imageFileList.value = [];
  }
};

const handleExceed: UploadProps['onExceed'] = () => {
  ElMessage.warning('请先删除现有图片后再上传新图片');
};

const previewImage = async (file: UploadUserFile) => {
  try {
    // 如果已有url，直接使用
    if ((file as any).url) {
      window.open((file as any).url, '_blank');
      return;
    }
    
    const serverCode = (file as any).serverCode || currentTable.value?.imageCode;
    if (serverCode) {
      const url = await fetchImageContent(serverCode);
      if (url) {
        // 更新文件的url属性，下次直接使用
        (file as any).url = url;
        window.open(url, '_blank');
      } else {
        ElMessage.error('预览失败');
      }
    }
  } catch(e) {
    console.error('预览失败:', e);
    ElMessage.error('预览失败');
  }
};

// 计算当前列类型
const currentColumnType = computed(() => {
  if (currentTable.value && editingColumnIndex.value >= 0) {
    return currentTable.value.columnTypes[editingColumnIndex.value] || 'text';
  }
  return 'text';
});

// 左侧列表数据（直接使用接口返回的数据）
const tableListData = computed(() => {
  return tableList.value;
});

const activeTableRowKey = computed(() => {
  if (activeTableIndex.value < 0 || activeTableIndex.value >= tableList.value.length) return null;
  const item = tableList.value[activeTableIndex.value];
  return item?.Id ?? item?.id ?? null;
});

// 当前表格的ID（用于判断是否已保存）
const currentTableId = computed(() => {
  if (activeTableIndex.value < 0) return null;
  const item = tableList.value[activeTableIndex.value];
  return item?.Id || item?.id || null;
});

// 处理查看按钮点击（设置为查看模式）
const handleTableEdit = (row: any) => {
  isEditMode.value = false; // 设置为查看模式
  handleTableSelect(row);
};


// 进入编辑模式
const enterEditMode = () => {
  isEditMode.value = true;
  ElMessage.success('已进入编辑模式');
};

const buildSectionTitleList = (table: TableData): string[] => {
  const sectionTitles = [table.sectionNames[-1] || ''];
  table.horizontalHeaders.forEach((_, index) => {
    sectionTitles.push(table.sectionNames[index] || '');
  });
  return sectionTitles;
};

const buildPrintListDataFromTable = (table: TableData) => {
  const horizontalHeaders = table.horizontalHeaders.length > 0 ? table.horizontalHeaders : [''];
  const verticalHeaders = table.verticalHeaders.length > 0 ? table.verticalHeaders : [''];
  const sectionTitles = buildSectionTitleList(table);

  const subRows = verticalHeaders.map((header) => {
    const row: Record<string, string> = {
      Header: header || ''
    };
    horizontalHeaders.forEach((_, index) => {
      row[`_${index + 1}`] = '';
    });
    return row;
  });

  return [{
    方向: table.printOrientation === true,
    记录编号: '',
    设备编号: '',
    环境温度: '',
    校核: '',
    记录: '',
    检验: '',
    计算: '',
    检验日期: '',
    检测原始数据检测项目: {
      名称: table.name || '',
      受控编号: table.controlNumber || '',
      实施日期: table.implementDate || '',
      检测对象类型: table.detectionObjectType || '',
      是否计算: table.isCalculate ?? false,
      检测原始数据检测项目特殊属性集合: table.specialAttributes.map((attr) => ({
        标题: attr.title,
        内容: '',
        选项: attr.options || [],
        选项json: attr.options?.length ? JSON.stringify(attr.options) : ''
      })),
      检测原始数据检测数据格式描述: {
        横向标题: horizontalHeaders,
        分区线: table.columnBorders,
        分区标题: sectionTitles,
        分区标题JSON: JSON.stringify(sectionTitles),
        纵向标题: verticalHeaders,
        纵向标题JSON: JSON.stringify(verticalHeaders)
      },
      检测原始数据详细数据主表集合: [{
        层数: '',
        建筑名称: '',
        显示摘要: '',
        检测原始数据详细数据子表集合: subRows
      }]
    }
  }];
};

// 打印列表 - 使用本地构造的打印数据
const handlePrintList = () => {
  if (!currentTableId.value) {
    ElMessage.warning('请先保存表格后再预览');
    return;
  }

  if (!currentTable.value) {
    ElMessage.warning('表格数据未加载');
    return;
  }

  // 使用本地构造的数据
  const cacheKey = `table-definition-print-${String(currentTableId.value)}-${Date.now()}`;
  detailStore.setPrintListData(cacheKey, buildPrintListDataFromTable(currentTable.value));

  router.push({
    path: getMenuChildPathByComponent(
      '/ParameterSettings/tableEditing/index.vue',
      '打印列表',
      '/参数设置/检测项目定义/打印列表'
    ),
    query: {
      id: String(currentTableId.value),
      cacheKey
    }
  });
};

// 退出编辑模式
const exitEditMode = () => {
  ElMessageBox.confirm('确定要取消编辑吗？未保存的修改将丢失', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning',
  }).then(() => {
    isEditMode.value = false;
    // 重新加载当前表格数据
    const currentItem = tableList.value[activeTableIndex.value];
    if (currentItem) {
      const itemId = currentItem.Id || currentItem.id;
      if (itemId) {
        handleTableSelect(currentItem);
      }
    }
    ElMessage.success('已退出编辑模式');
  }).catch(() => { /* cancelled */ });
};

// 处理删除按钮点击
const handleTableDelete = (row: any) => {
  const index = findTableIndex(row);
  if (index >= 0) deleteTable(index);
};


// 监听 tableListData 变化
watch(() => tableListData.value, () => {
  if (suppressTableListAutoSelect.value) return;
  // 数据更新时，如果当前选中的表格还存在，保持选中状态
  if (activeTableIndex.value >= 0 && activeTableIndex.value < tableList.value.length) {
    const currentItem = tableList.value[activeTableIndex.value];
    if (currentItem) {
      handleTableSelect(currentItem);
    }
  }
}, { deep: true, immediate: true });

// 加载表格列表数据（仅用于左侧列表展示）
const loadTableList = async () => {
  try {
    listLoading.value = true; // 开始加载
    // 构建分页参数
    const paginationData = {
      page: pagination.value.page,
      intPageSize: pagination.value.pageSize
    };
    // 将分页参数作为第二个参数，筛选参数作为第三个参数传递
    const res = await ParameterSettings.parameterSettingsListIndex(paginationData, filterParams.value);
    
    if (res && res.status === 200 && res.success && res.response && res.response.data) {
      // 直接使用接口返回的数据，不做复杂转换
      tableList.value = Array.isArray(res.response.data) ? res.response.data : [];
      // 更新总数 - 使用dataCount字段
      pagination.value.total = res.response.dataCount || res.response.total || res.response.count || 0;
      // 从接口返回中提取model数据
      if (res.response.model && typeof res.response.model === 'object') {
        tableModel.value = res.response.model;
      } else {
        tableModel.value = undefined;
      }
    } else {
      tableList.value = [];
      tableModel.value = undefined;
      pagination.value.total = 0;
    }
  } catch (error) {
    console.error('加载表格列表失败:', error);
    tableList.value = [];
    pagination.value.total = 0;
  } finally {
    listLoading.value = false; // 加载完成
  }
};

const loadDeviceTypeOptions = async () => {
  try {
    deviceTypeLoading.value = true;
    const res = await ParameterSettings.parameterSettingsDeviceTypeList(
      { page: 1, intPageSize: 50 },
      []
    );

    if (res && res.status === 200 && res.success && Array.isArray(res.response?.data)) {
      deviceTypeOptions.value = res.response.data
        .filter((item: any) => !item.ISDELETED && item.名称)
        .map((item: any) => ({
          名称: String(item.名称),
          提示词: item.提示词 || '',
          Id: item.Id
        }));
      if (currentTable.value && !currentTableId.value && !currentTable.value.deviceType) {
        currentTable.value.deviceType = getDefaultDeviceType();
      }
    } else {
      deviceTypeOptions.value = [];
    }
  } catch (error) {
    console.error('加载设备类型失败:', error);
    deviceTypeOptions.value = [];
  } finally {
    deviceTypeLoading.value = false;
  }
};

// 筛选处理函数（参考 OriginalDetectionList 的逻辑）
const handleFilter = (filters: any[]) => {
  console.log('筛选和排序参数:', filters);
  filterParams.value = filters;
  pagination.value.page = 1; // 筛选时重置到第一页
  loadTableList(); // 重新加载数据
};

// 分页器事件处理
const handlePageChange = (page: number) => {
  pagination.value.page = page;
  loadTableList();
};

const handlePageSizeChange = (pageSize: number) => {
  pagination.value.pageSize = pageSize;
  pagination.value.page = 1; // 改变页大小时重置到第一页
  loadTableList();
};

// 加载表格详情数据（用于右侧编辑区域）
const loadTableDetail = async (item: any) => {
  return parseTableDetail(item);
};

// 标记是否已完成首次加载
const isFirstLoad = ref(true);

// 组件挂载后加载数据
onMounted(async () => {
  await Promise.all([
    loadTableList(),
    loadDeviceTypeOptions()
  ]);
  isFirstLoad.value = false;
});

// 标签页切换回来时恢复选中状态（keep-alive 激活时触发）
onActivated(async () => {
  // 首次加载时 onMounted 已处理，跳过
  if (isFirstLoad.value) {
    return;
  }
  
  const savedId = pageStateStore.getSelectedId(route.path);
  if (savedId) {
    // 等待列表加载完成（如果正在加载）
    if (listLoading.value) {
      await new Promise<void>(resolve => {
        const unwatch = watch(listLoading, (loading) => {
          if (!loading) {
            unwatch();
            resolve();
          }
        });
      });
    }
    
    // 如果列表为空，重新加载
    if (tableList.value.length === 0) {
      await loadTableList();
    }
    
    // 在列表中查找对应的项目并加载详情
    const targetItem = tableList.value.find(item => 
      (item.Id || item.id) === savedId
    );
    if (targetItem) {
      await handleTableSelect(targetItem);
    }
  }
});

watch(() => currentTable.value?.name, (value) => {
  if (String(value || '').trim()) {
    showNameError.value = false;
  }
});

watch(() => currentTable.value?.controlNumber, (value) => {
  if (String(value || '').trim()) {
    showControlNumberError.value = false;
  }
});

watch(() => currentTable.value?.numberSuffix, (value) => {
  if (String(value || '').trim()) {
    showNumberSuffixError.value = false;
  }
});

watch(() => currentTable.value?.implementDate, (value) => {
  if (value) {
    showImplementDateError.value = false;
  }
});

// 添加新表格
const addNewTable = () => {
  clearRequiredFieldErrors();
  const tableName = `表格${tableList.value.length + 1}`;
  const newTable = createEmptyTable(tableName);
  newTable.deviceType = getDefaultDeviceType();
  
  // 创建列表项数据结构（用于左侧列表显示）
  const newListItem: any = {
    名称: tableName,
    编码: `TB${String(tableList.value.length + 1).padStart(4, '0')}`,
    _isNew: true,
    _tableData: newTable
  };
  
  tableList.value.push(newListItem);
  activeTableIndex.value = tableList.value.length - 1;
  currentTable.value = newTable;
  isEditMode.value = true;
  
  ElMessage.success('新增表格成功');
};

// 复制表格：完整复制表格结构与所有表单录入区域的值，生成一条新记录并进入编辑模式
const copyTable = async (row: any) => {
  clearRequiredFieldErrors();
  const index = findTableIndex(row);
  if (index < 0) return;

  const item = tableList.value[index];
  const itemId = item.Id || item.id;

  let sourceData: TableData | null = null;

  try {
    loading.value = true;
    if (item._isNew && item._tableData) {
      // 本地新增未保存的表格：直接复制内存数据
      sourceData = item._tableData;
    } else if (itemId) {
      // 已保存的表格：调用详情接口获取完整结构（含特殊属性）
      const res = await ParameterSettings.parameterSettingsById({ id: itemId });
      if (res && res.status === 200 && res.success && res.response) {
        sourceData = await loadTableDetail(res.response);
      } else {
        ElMessage.error('获取原记录数据失败');
        return;
      }
    } else {
      ElMessage.warning('该记录暂无可复制的数据');
      return;
    }

    if (!sourceData) {
      ElMessage.error('复制失败：未获取到数据');
      return;
    }

    // 深拷贝原记录，避免新记录与原记录共享引用
    const cloned = deepCloneTableData(sourceData);
    // 生成新记录名称（避免重名）
    cloned.name = generateCopyName(sourceData.name || item.名称 || item.name || '');
    // 清空原始数据ID：复制出来的是一条全新记录，保存时应走新增逻辑
    cloned.originalDataFormatDescId = undefined;

    const newListItem: any = {
      名称: cloned.name,
      编码: `TB${String(tableList.value.length + 1).padStart(4, '0')}`,
      _isNew: true,
      _tableData: cloned
    };

    tableList.value.push(newListItem);
    activeTableIndex.value = tableList.value.length - 1;
    currentTable.value = cloned;
    isEditMode.value = true;

    ElMessage.success(`已复制为「${cloned.name}」，请修改后保存`);
  } catch (error) {
    console.error('复制表格失败:', error);
    ElMessage.error('复制失败，请重试');
  } finally {
    loading.value = false;
  }
};

// 深拷贝表格数据（含嵌套的数组与对象）
const deepCloneTableData = (data: TableData): TableData => {
  try {
    return JSON.parse(JSON.stringify(data)) as TableData;
  } catch {
    return { ...data };
  }
};

// 生成副本名称：原名 + "副本"，若已存在则追加序号
const generateCopyName = (baseName: string): string => {
  const base = `${baseName || '表格'}副本`;
  const existingNames = new Set(
    tableList.value.map((item: any) => item.名称 || item.name || '')
  );
  if (!existingNames.has(base)) return base;
  let suffix = 2;
  while (existingNames.has(`${base}${suffix}`)) {
    suffix += 1;
  }
  return `${base}${suffix}`;
};

// 选择表格（通过ID加载详情数据）
const handleTableSelect = async (row: any) => {
  clearRequiredFieldErrors();
  const index = findTableIndex(row);
  
  if (index >= 0) {
    activeTableIndex.value = index;
    const item = tableList.value[index];
    
    // 保存当前选中的项目ID到store（用于标签页切换时恢复）
    const itemId = item.Id || item.id;
    if (itemId) {
      pageStateStore.setSelectedId(route.path, itemId);
    }
    
    // 如果是新添加的表格且有_tableData，直接使用
    if (item._isNew && item._tableData) {
      currentTable.value = item._tableData;
      return;
    }
    
    // 如果有ID，调用详情接口加载数据
    if (itemId) {
      try {
        loading.value = true; // 开始加载
        // 调用详情接口
        const res = await ParameterSettings.parameterSettingsById({ id: itemId });
        
        if (res && res.status === 200 && res.success && res.response) {
          // 使用 loadTableDetail 函数解析详情数据
          const tableData = await loadTableDetail(res.response);
          currentTable.value = tableData;
          ElMessage.success('数据加载成功');
        } else {
          ElMessage.error('数据加载失败');
          currentTable.value = createEmptyTable(item.名称 || item.name);
        }
      } catch (error) {
        console.error('加载表格详情失败:', error);
        ElMessage.error('数据加载失败');
        currentTable.value = createEmptyTable(item.名称 || item.name);
      } finally {
        loading.value = false; // 加载完成
      }
    } else {
      // 如果没有ID（可能是新增的表格），检查是否已有完整数据
      if (item.verticalHeaders && item.horizontalHeaders) {
        // 如果已有完整数据，直接使用
        currentTable.value = item as TableData;
      } else {
        currentTable.value = createEmptyTable(item.名称 || item.name);
      }
    }
  }
};

// 更新表格名称（如果接口数据中有 name 或 名称 字段）
const updateTableName = (index: number) => {
  const item = tableList.value[index];
  if (item) {
    const nameField = item.name || item.名称;
    if (nameField && typeof nameField === 'string' && !nameField.trim()) {
      item.name = item.名称 = `表格${index + 1}`;
    }
  }
};

// 删除表格
const deleteTable = async (index: number) => {
  const item = tableList.value[index];
  if (!item) return;

  const itemId = item.Id || item.id;
  
  // 如果是新建的表格（没有ID），直接从前端删除
  if (!itemId || item._isNew) {
    ElMessageBox.confirm('确定要删除这个表格吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning',
    }).then(() => {
      tableList.value.splice(index, 1);
      if (activeTableIndex.value === index) {
        activeTableIndex.value = -1;
        currentTable.value = null;
      }
      ElMessage.success('删除成功');
    }).catch(() => { /* cancelled */ });
    return;
  }

  // 有ID的情况，调用后端API删除
  ElMessageBox.confirm('确定要删除这个表格吗？删除后无法恢复', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning',
  }).then(async () => {
    try {
      // 调用删除接口
      const res = await ParameterSettings.parameterSettingsDelete({ id: itemId });
      
      if (res && res.status === 200 && res.success) {
        const isDeletingActive = activeTableIndex.value === index;
        const activeItem = activeTableIndex.value >= 0 && activeTableIndex.value < tableList.value.length
          ? tableList.value[activeTableIndex.value]
          : null;
        const activeItemId = activeItem?.Id ?? activeItem?.id;

        // 如果删除的是当前选中的表格，清空选中状态
        if (isDeletingActive) {
          activeTableIndex.value = -1;
          currentTable.value = null;
          pageStateStore.clearSelectedId(route.path);
        } else if (activeTableIndex.value > index) {
          // 如果删除的项在当前选中项之前，需要调整索引
          activeTableIndex.value--;
        }
        
        if (tableList.value.length === 1 && pagination.value.page > 1) {
          pagination.value.page--;
        }
        suppressTableListAutoSelect.value = true;
        try {
          await loadTableList();
          if (!isDeletingActive && activeItemId) {
            const foundItem = findTableItem(activeItemId);
            if (foundItem) {
              await handleTableSelect(foundItem);
            }
          }
        } finally {
          suppressTableListAutoSelect.value = false;
        }
        ElMessage.success('删除成功');
      } else {
        ElMessage.error('删除失败');
      }
    } catch (error) {
      console.error('删除表格失败:', error);
      ElMessage.error('删除失败');
    }
  }).catch(() => { /* cancelled */ });
};

// 增加横向标题（列）
const addColumn = () => {
  if (!currentTable.value) return;
  const newColIndex = currentTable.value.horizontalHeaders.length;
  currentTable.value.horizontalHeaders.push(`列${newColIndex + 1}`);
  currentTable.value.columnTypes.push('number');
  currentTable.value.selectOptions.push([]);
  currentTable.value.autoFillRowCounts.push(0);
  currentTable.value.columnBorders.push(false);
  // sectionNames 是 Record<number, string>，新列默认没有分区名称，不需要添加
  // 为数据区域的每行添加空单元格
  currentTable.value.data.forEach(row => {
    row.push('');
  });
  ElMessage.success('增加横向标题成功'); 
};

// 删除横向标题（列）
const deleteColumn = (colIndex: number) => {
  if (!currentTable.value || currentTable.value.horizontalHeaders.length <= 1) {
    ElMessage.warning('至少保留一列');
    return;
  }
  currentTable.value.horizontalHeaders.splice(colIndex, 1);
  currentTable.value.columnTypes.splice(colIndex, 1);
  currentTable.value.selectOptions.splice(colIndex, 1);
  currentTable.value.autoFillRowCounts.splice(colIndex, 1);
  currentTable.value.columnBorders.splice(colIndex, 1);
  // 删除该列的分区名称，并重新映射后续列的索引
  delete currentTable.value.sectionNames[colIndex];
  const newSectionNames: Record<number, string> = {};
  if (currentTable.value.sectionNames[-1]) {
    newSectionNames[-1] = currentTable.value.sectionNames[-1];
  }
  Object.keys(currentTable.value.sectionNames).forEach(key => {
    const idx = Number(key);
    if (idx >= 0 && idx !== colIndex) {
      const newIdx = idx > colIndex ? idx - 1 : idx;
      if (currentTable.value!.sectionNames[idx]) {
        newSectionNames[newIdx] = currentTable.value!.sectionNames[idx];
      }
    }
  });
  currentTable.value.sectionNames = newSectionNames;
  currentTable.value.data.forEach(row => {
    row.splice(colIndex, 1);
  });
  ElMessage.success('删除横向标题成功');
};

// 更新横向标题
const updateColumnHeader = (colIndex: number, value: string) => {
  if (currentTable.value) {
    currentTable.value.horizontalHeaders[colIndex] = value;
  }
};

// 更新竖向标题
const updateVerticalHeader = (rowIndex: number, value: string) => {
  if (currentTable.value) {
    currentTable.value.verticalHeaders[rowIndex] = value;
  }
};

// 增加竖向标题（行）
const addRow = () => {
  if (!currentTable.value) return;
  currentTable.value.verticalHeaders.push(`行${currentTable.value.verticalHeaders.length + 1}`);
  // 为数据区域添加新行
  const newRow = new Array(currentTable.value.horizontalHeaders.length).fill('');
  currentTable.value.data.push(newRow);
  currentTable.value.columnTypes.forEach((type, colIndex) => {
    if (type === 'preset') {
      syncPresetColumn(colIndex);
    }
  });
  ElMessage.success('增加竖向标题成功');
};

// 删除竖向标题（行）
const deleteRow = (rowIndex: number) => {
  if (!currentTable.value || currentTable.value.verticalHeaders.length <= 1) {
    ElMessage.warning('至少保留一行');
    return;
  }
  currentTable.value.verticalHeaders.splice(rowIndex, 1);
  currentTable.value.data.splice(rowIndex, 1);
  currentTable.value.columnTypes.forEach((type, colIndex) => {
    if (type === 'preset') {
      syncPresetColumn(colIndex);
    }
  });
  ElMessage.success('删除竖向标题成功');
};

// 更新单元格
const updateCell = (rowIndex: number, colIndex: number, value: string) => {
  if (currentTable.value && currentTable.value.data[rowIndex]) {
    currentTable.value.data[rowIndex][colIndex] = value;
  }
};

// 清空表格
const clearTable = () => {
  ElMessageBox.confirm('确定要清空表格内容吗？', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning',
  }).then(() => {
    if (currentTable.value) {
      const emptyTable = createEmptyTable(currentTable.value.name);
      Object.assign(currentTable.value, emptyTable);
    }
    ElMessage.success('清空成功');
  }).catch(() => { /* cancelled */ });
};

// 添加特殊属性（isSingle=true 为单条，false 为全局）
const addSpecialAttribute = (isSingle = false) => {
  if (!currentTable.value) return;
  currentTable.value.specialAttributes.push({ title: '', options: [], isSingle });
  ElMessage.success(isSingle ? '添加单条字段成功' : '添加全局字段成功');
};

// 删除特殊属性
const deleteSpecialAttribute = (index: number) => {
  if (!currentTable.value) return;
  currentTable.value.specialAttributes.splice(index, 1);
  ElMessage.success('删除特殊属性成功');
};

// 更新特殊属性标题
const updateSpecialAttribute = (index: number, value: string) => {
  if (currentTable.value) {
    currentTable.value.specialAttributes[index].title = value;
  }
};

// 切换属性作用域（全局 / 单条）
const toggleAttributeScope = (index: number) => {
  if (!currentTable.value) return;
  const attr = currentTable.value.specialAttributes[index];
  attr.isSingle = !attr.isSingle;
  ElMessage.success(attr.isSingle ? '已切换为单条字段' : '已切换为全局字段');
};

// 特殊属性选项弹框相关
const optionsDialogVisible = ref(false);
const currentOptionsIndex = ref(-1);
const currentOptions = ref<string[]>([]);

// 打开选项编辑弹框
const openOptionsDialog = (index: number) => {
  if (!currentTable.value) return;
  currentOptionsIndex.value = index;
  currentOptions.value = [...currentTable.value.specialAttributes[index].options];
  optionsDialogVisible.value = true;
};

// 添加选项
const addOption = () => {
  currentOptions.value.push('');
};

// 删除选项
const deleteOption = (index: number) => {
  currentOptions.value.splice(index, 1);
};

// 保存选项
const saveOptions = () => {
  if (!currentTable.value || currentOptionsIndex.value < 0) return;
  // 过滤空选项
  currentTable.value.specialAttributes[currentOptionsIndex.value].options = 
    currentOptions.value.filter(opt => opt.trim() !== '');
  optionsDialogVisible.value = false;
  ElMessage.success('选项保存成功');
};

// 特殊属性唯一ID映射（用于稳定的 key）
const specialAttrIdMap = ref<Map<number, string>>(new Map());
let specialAttrIdCounter = 0;

// 生成唯一ID
const generateAttrId = (): string => {
  return `attr_${++specialAttrIdCounter}_${Date.now()}`;
};

interface AttrWithId {
  id: string;
  originalIndex: number;
  title: string;
  options: string[];
  isSingle?: boolean;
}

// 全局属性列表（isSingle 为 false/缺省，左侧区域渲染）
const globalAttributesWithId = computed<AttrWithId[]>(() => {
  if (!currentTable.value) return [];
  const attrs = currentTable.value.specialAttributes;
  const result: AttrWithId[] = [];
  attrs.forEach((attr, index) => {
    if (attr.isSingle === true) return;
    if (!specialAttrIdMap.value.has(index)) {
      specialAttrIdMap.value.set(index, generateAttrId());
    }
    result.push({
      id: specialAttrIdMap.value.get(index)!,
      originalIndex: index,
      title: attr.title,
      options: attr.options,
      isSingle: attr.isSingle
    });
  });
  return result;
});

// 单条属性列表（isSingle 为 true，右侧区域渲染）
const singleAttributesWithId = computed<AttrWithId[]>(() => {
  if (!currentTable.value) return [];
  const attrs = currentTable.value.specialAttributes;
  const result: AttrWithId[] = [];
  attrs.forEach((attr, index) => {
    if (attr.isSingle !== true) return;
    if (!specialAttrIdMap.value.has(index)) {
      specialAttrIdMap.value.set(index, generateAttrId());
    }
    result.push({
      id: specialAttrIdMap.value.get(index)!,
      originalIndex: index,
      title: attr.title,
      options: attr.options,
      isSingle: attr.isSingle
    });
  });
  return result;
});

// 特殊属性拖拽排序相关
const dragIndex = ref<number>(-1);
const dragOverIndex = ref<number>(-1);

const handleDragStart = (e: DragEvent, index: number) => {
  dragIndex.value = index;
  if (e.dataTransfer) {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  }
};

const handleDragEnd = () => {
  dragIndex.value = -1;
  dragOverIndex.value = -1;
};

const handleDragOver = (e: DragEvent, index: number) => {
  e.preventDefault();
  if (dragIndex.value !== -1 && dragIndex.value !== index) {
    dragOverIndex.value = index;
  }
};

const handleDragLeave = () => {
  dragOverIndex.value = -1;
};

const handleDrop = (e: DragEvent, targetIndex: number) => {
  e.preventDefault();
  if (!currentTable.value || dragIndex.value === -1 || dragIndex.value === targetIndex) {
    handleDragEnd();
    return;
  }

  const sourceIndex = dragIndex.value;
  const attrs = currentTable.value.specialAttributes;

  // 只允许同类型（全局/单条）内部排序，跨类型不处理
  const sourceIsSingle = attrs[sourceIndex].isSingle === true;
  const targetIsSingle = attrs[targetIndex].isSingle === true;
  if (sourceIsSingle !== targetIsSingle) {
    handleDragEnd();
    return;
  }

  // 同步移动 ID 映射
  const idMap = specialAttrIdMap.value;
  const sourceId = idMap.get(sourceIndex);
  const newIdMap = new Map<number, string>();

  // 移动元素
  const [movedItem] = attrs.splice(sourceIndex, 1);
  attrs.splice(targetIndex, 0, movedItem);

  // 重建 ID 映射，保持每个属性的 ID 不变
  attrs.forEach((_, index) => {
    if (index === targetIndex) {
      newIdMap.set(index, sourceId!);
    } else if (sourceIndex < targetIndex) {
      // 向后移动：sourceIndex 到 targetIndex-1 的元素向前移一位
      if (index >= sourceIndex && index < targetIndex) {
        newIdMap.set(index, idMap.get(index + 1)!);
      } else {
        newIdMap.set(index, idMap.get(index)!);
      }
    } else {
      // 向前移动：targetIndex+1 到 sourceIndex 的元素向后移一位
      if (index > targetIndex && index <= sourceIndex) {
        newIdMap.set(index, idMap.get(index - 1)!);
      } else {
        newIdMap.set(index, idMap.get(index)!);
      }
    }
  });

  specialAttrIdMap.value = newIdMap;

  handleDragEnd();
  ElMessage.success('排序已更新');
};

// 打开列类型设置对话框
const openColumnTypeDialog = (colIndex: number) => {
  editingColumnIndex.value = colIndex;
  editingAutoFillRows.value = Math.max(0, Number(currentTable.value?.autoFillRowCounts?.[colIndex]) || 0);
  showColumnTypeDialog.value = true;
};

// 确认设置列类型
const confirmColumnType = (type: ColumnType) => {
  if (currentTable.value && editingColumnIndex.value >= 0) {
    const colIndex = editingColumnIndex.value;
    const oldType = currentTable.value.columnTypes[colIndex];
    currentTable.value.columnTypes[colIndex] = type;
    if (!currentTable.value.autoFillRowCounts) currentTable.value.autoFillRowCounts = [];
    currentTable.value.autoFillRowCounts[colIndex] = Math.max(0, Number(editingAutoFillRows.value) || 0);
    
    // 下拉选择框与表格预设值列均需要配置选项。
    if (type === 'select' || type === 'preset') {
      if (type === 'preset') {
        if (oldType !== 'preset') {
          currentTable.value.selectOptions[colIndex] = currentTable.value.data.map(row => String(row[colIndex] || ''));
        }
      }
      showColumnTypeDialog.value = false;
      editingColumnIndex.value = -1;
      // 延迟打开选项编辑对话框，确保列类型设置对话框已关闭
      setTimeout(() => {
        openSelectOptionsDialog(colIndex);
      }, 100);
      return;
    }
    
    // 列类型发生变化时，清空该列所有单元格的值
    if (oldType !== type) {
      currentTable.value.data.forEach((row) => {
        row[colIndex] = '';
      });
    }
    
    ElMessage.success('设置成功');
  }
  showColumnTypeDialog.value = false;
  editingColumnIndex.value = -1;
};

// 取消设置列类型
const cancelColumnType = () => {
  showColumnTypeDialog.value = false;
  editingColumnIndex.value = -1;
};

// 打开下拉选项编辑对话框
const openSelectOptionsDialog = (colIndex: number) => {
  if (!currentTable.value) return;
  editingSelectOptionsColumnIndex.value = colIndex;
  const isPresetColumn = currentTable.value.columnTypes[colIndex] === 'preset';
  const currentOptions = currentTable.value.selectOptions[colIndex];
  if (isPresetColumn) {
    const rowCount = currentTable.value.verticalHeaders.length;
    editingSelectOptions.value = Array.from({ length: rowCount }, (_, rowIndex) => (
      String(currentOptions?.[rowIndex] ?? currentTable.value?.data[rowIndex]?.[colIndex] ?? '')
    ));
  } else {
    // 获取当前列的选项，如果没有则使用默认值
    const dialogOptions = currentOptions && currentOptions.length > 0
      ? [...currentOptions]
      : ['合格', '不合格', '待定'];
    editingSelectOptions.value = colIndex === 0 ? dialogOptions : stripSelectOptionImages(dialogOptions);
  }
  refreshSelectOptionThumbnails();
  showSelectOptionsDialog.value = true;
};

// 添加下拉选项
const addSelectOption = () => {
  editingSelectOptions.value.push('');
  setSelectOptionImageMeta(editingSelectOptions.value.length - 1, {});
};

// 删除下拉选项
const removeSelectOption = (index: number) => {
  if (editingSelectOptions.value.length > 1) {
    editingSelectOptions.value.splice(index, 1);
    refreshSelectOptionThumbnails();
  }
};

// 验证选项
const validateOption = (index: number) => {
  const { text, imageCode } = splitSelectOptionValue(editingSelectOptions.value[index] || '');
  const nextImageCode = canUploadSelectOptionImage.value ? imageCode : '';
  if (!text) {
    editingSelectOptions.value[index] = buildSelectOptionValue(`选项${index + 1}`, nextImageCode);
    return;
  }
  editingSelectOptions.value[index] = buildSelectOptionValue(text, nextImageCode);
};

// 确认保存下拉选项
const confirmSelectOptions = () => {
  if (!currentTable.value || editingSelectOptionsColumnIndex.value < 0) return;
  const colIndex = editingSelectOptionsColumnIndex.value;

  if (isPresetValueColumn.value) {
    const rowCount = currentTable.value.verticalHeaders.length;
    if (editingSelectOptions.value.length !== rowCount) {
      ElMessage.warning(`预设值数量必须与表格行数一致（当前共 ${rowCount} 行）`);
      return;
    }

    const presetValues = editingSelectOptions.value.map(value => String(value || '').trim());
    if (presetValues.some(value => !value)) {
      ElMessage.warning('请为每一行填写预设值');
      return;
    }

    currentTable.value.selectOptions[colIndex] = presetValues;
    syncPresetColumn(colIndex);
    ElMessage.success('表格预设值已保存');
    cancelSelectOptions();
    return;
  }
  
  // 过滤空选项并去除空格
  const validOptions = editingSelectOptions.value
    .map((opt, index) => {
      const { text, imageCode } = splitSelectOptionValue(opt);
      return buildSelectOptionValue(text || `选项${index + 1}`, canUploadSelectOptionImage.value ? imageCode : '');
    })
    .filter(opt => getSelectOptionText(opt) !== '');
  
  if (validOptions.length === 0) {
    ElMessage.warning('至少需要保留一个选项');
    return;
  }
  
  // 检查是否有重复选项，按选项文本去重，图片绑定随首次出现的选项保留
  const seenOptionTexts = new Set<string>();
  const uniqueOptions = validOptions.filter(option => {
    const optionText = getSelectOptionText(option);
    if (seenOptionTexts.has(optionText)) return false;
    seenOptionTexts.add(optionText);
    return true;
  });
  if (uniqueOptions.length !== validOptions.length) {
    ElMessage.warning('存在重复选项，已自动去重');
  }
  
  currentTable.value.selectOptions[colIndex] = uniqueOptions;
  ElMessage.success('保存成功');
  showSelectOptionsDialog.value = false;
  editingSelectOptionsColumnIndex.value = -1;
  editingSelectOptions.value = [];
  selectOptionImageMeta.value = {};
};

// 取消编辑下拉选项
const cancelSelectOptions = () => {
  showSelectOptionsDialog.value = false;
  editingSelectOptionsColumnIndex.value = -1;
  editingSelectOptions.value = [];
  selectOptionImageMeta.value = {};
  selectOptionPreviewUrl.value = '';
};

// 设置第一分区名称（从第一列开始的分区，不需要分界线）
// 使用 sectionNames[-1] 存储第一分区名称
const setFirstSectionName = async () => {
  if (!currentTable.value) return;
  
  const hasName = currentTable.value.sectionNames[-1];
  
  if (hasName) {
    // 取消分区名称
    delete currentTable.value.sectionNames[-1];
    ElMessage.success('已取消第一分区名称');
  } else {
    // 设置分区名称
    try {
      const { value } = await ElMessageBox.prompt('请输入第一分区名称', '设置分区名称', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputPlaceholder: '例如：基本信息、检测数据',
        inputValue: ''
      });
      currentTable.value.sectionNames[-1] = value || '';
      nextTick(() => {
        setTimeout(updateSectionNames, 50);
      });
      ElMessage.success('已设置第一分区名称');
    } catch {
      // 用户取消
    }
  }
};

// 切换列边界线（不弹出分区名称对话框）
const toggleColumnBorderOnly = (colIndex: number) => {
  if (!currentTable.value) return;

  const isCurrentlySet = currentTable.value.columnBorders[colIndex];

  if (isCurrentlySet) {
    // 取消分界线
    currentTable.value.columnBorders[colIndex] = false;
    currentTable.value.sectionNames[colIndex] = '';
    ElMessage.success('已取消列边界线');
  } else {
    // 设置分界线，不设置分区名称
    currentTable.value.columnBorders[colIndex] = true;
    currentTable.value.sectionNames[colIndex] = '';
    ElMessage.success('已设置列边界线');
  }
};

// 切换列边界线
// columnBorders[colIndex] = 第 colIndex 列与第 colIndex+1 列之间的分界线
const toggleColumnBorder = async (colIndex: number) => {
  if (!currentTable.value) return;

  const isCurrentlySet = currentTable.value.columnBorders[colIndex];

  if (isCurrentlySet) {
    // 取消分界线
    currentTable.value.columnBorders[colIndex] = false;
    currentTable.value.sectionNames[colIndex] = '';
    ElMessage.success('已取消列边界线');
  } else {
    // 设置分界线，弹出对话框输入右侧分区名称
    try {
      const { value } = await ElMessageBox.prompt('请输入右侧分区名称', '设置分界线', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputPlaceholder: '例如：基本信息、检测数据',
        inputValue: currentTable.value.sectionNames[colIndex] || ''
      });
      currentTable.value.columnBorders[colIndex] = true;
      currentTable.value.sectionNames[colIndex] = value || '';
      // 设置分界线后更新分区名称位置
      nextTick(() => {
        setTimeout(updateSectionNames, 50);
      });
      ElMessage.success('已设置列边界线');
    } catch {
      // 用户取消
    }
  }
};

// 处理列边界线右键点击 - 在指定位置插入新列
const handleColumnBorderRightClick = (event: MouseEvent, colIndex: number) => {
  event.preventDefault();
  if (!currentTable.value) return;
  
  ElMessageBox.confirm('确定要在此位置插入新列吗？', '插入列', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'info',
  }).then(() => {
    insertColumnAt(colIndex + 1);
  }).catch(() => { /* cancelled */ });
};

// 在指定位置插入新列
const insertColumnAt = (insertIndex: number) => {
  if (!currentTable.value) return;
  
  // 在横向标题中插入新列
  currentTable.value.horizontalHeaders.splice(insertIndex, 0, `列${insertIndex + 1}`);
  
  // 在列类型中插入默认类型
  currentTable.value.columnTypes.splice(insertIndex, 0, 'number');
  
  // 在列边界线中插入默认值
  currentTable.value.columnBorders.splice(insertIndex, 0, false);
  
  // 重新映射分区名称索引（插入位置及之后的索引都要+1）
  const newSectionNames: Record<number, string> = {};
  if (currentTable.value.sectionNames[-1]) {
    newSectionNames[-1] = currentTable.value.sectionNames[-1];
  }
  Object.keys(currentTable.value.sectionNames).forEach(key => {
    const idx = Number(key);
    if (idx >= 0) {
      const newIdx = idx >= insertIndex ? idx + 1 : idx;
      if (currentTable.value!.sectionNames[idx]) {
        newSectionNames[newIdx] = currentTable.value!.sectionNames[idx];
      }
    }
  });
  currentTable.value.sectionNames = newSectionNames;
  
  // 在下拉选项中插入空数组
  currentTable.value.selectOptions.splice(insertIndex, 0, []);
  currentTable.value.autoFillRowCounts.splice(insertIndex, 0, 0);
  
  // 在每一行数据中插入空单元格
  currentTable.value.data.forEach(row => {
    row.splice(insertIndex, 0, '');
  });
  
  ElMessage.success(`已在第 ${insertIndex + 1} 列位置插入新列`);
};

// 收集并格式化表格数据并保存
const saveTableData = async () => {
  if (!currentTable.value) {
    ElMessage.warning('请先选择或创建表格');
    return;
  }

  if (!validateRequiredFields()) {
    return;
  }

  const incompletePresetColumn = currentTable.value.columnTypes.findIndex((type, colIndex) => {
    if (type !== 'preset') return false;
    const presetOptions = currentTable.value!.selectOptions[colIndex] || [];
    return presetOptions.length !== currentTable.value!.verticalHeaders.length
      || presetOptions.some(value => !String(value || '').trim());
  });
  if (incompletePresetColumn >= 0) {
    ElMessage.warning(`请先完善“${currentTable.value.horizontalHeaders[incompletePresetColumn]}”列的表格预设值`);
    return;
  }

  const currentItem = activeTableIndex.value >= 0 && activeTableIndex.value < tableList.value.length
    ? tableList.value[activeTableIndex.value]
    : null;
  
  const itemId = currentItem?.Id || currentItem?.id;
  const isNew = currentItem?._isNew || !itemId;
  normalizeSelectOptionImages(currentTable.value);
  const submitData = formatTableDataForSave(currentTable.value, itemId, isNew);

  try {
    // 调用保存接口
    const res = await ParameterSettings.parameterSettingsSave(submitData);
    
    if (res && res.status === 200 && res.success) {
      if (isNew) {
        ElMessage.success('新增成功');
        // 新增成功后，获取返回的ID（后端返回格式：response.ID）
        const newId = res.response?.ID || res.response?.Id || res.response?.id;
        // 保存当前编辑的名称，用于刷新后重新定位
        const targetName = currentTable.value?.name;
        
        // 新增成功后刷新列表以获取最新的数据
        pagination.value.page = 1;
        suppressTableListAutoSelect.value = true;
        try {
          await loadTableList();
          // 刷新列表后，重新选中当前项（优先通过ID匹配，其次通过名称匹配）
          const foundItem = newId ? findTableItem(newId) : findTableItem(undefined, targetName);
          if (foundItem) {
            await handleTableSelect(foundItem);
          } else if (newId) {
            const detailRes = await ParameterSettings.parameterSettingsById({ id: newId });
            if (detailRes && detailRes.status === 200 && detailRes.success && detailRes.response) {
              currentTable.value = await loadTableDetail(detailRes.response);
              pageStateStore.setSelectedId(route.path, newId);
              activeTableIndex.value = -1;
            }
          }
        } finally {
          suppressTableListAutoSelect.value = false;
        }
        // 保存成功后退出编辑模式
        isEditMode.value = false;
      } else {
        ElMessage.success('更新成功');
        // 更新成功后，重新加载列表和当前项数据以获取最新数据
        await loadTableList();
        // 重新查找并选中当前项
        const foundItem = findTableItem(itemId, currentTable.value?.name);
        if (foundItem) {
          await handleTableSelect(foundItem);
          // 保存成功后退出编辑模式
          isEditMode.value = false;
        }
      }
    } else {
      ElMessage.error(isNew ? '新增失败' : '更新失败');
    }
  } catch (error: unknown) {
    console.error('保存表格数据失败:', error);
    // 拦截器已处理业务错误提示，这里不再重复提示
  }
};
</script>

<style lang="less" scoped>
.table-editor-container {
  /* 页面 padding / 背景 / 滚动由 layout 统一处理 */
  height: 100%;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  
  .el-card {
    flex: 1;
    height: 0; // 配合flex: 1使用，确保高度正确
    max-height: 100%;
    display: flex;
    flex-direction: column;
    
    :deep(.el-card__body) {
      flex: 1;
      height: 0; // 配合flex: 1使用，确保高度正确
      padding: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      min-height: 0; // 重要：允许flex子元素缩小
    }
  }
  
  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    
    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 500;
    }
  }
  
  .main-content {
    display: flex;
    flex: 1;
    height: 0; // 配合flex: 1使用，确保高度正确
    min-height: 0; // 重要：允许flex子元素缩小
    overflow: hidden;
    
    .resize-handle {
      width: 6px;
      background: #e4e7ed;
      cursor: col-resize;
      flex-shrink: 0;
      transition: background-color 0.2s;
      position: relative;
      &:hover { background: #409eff; }
      &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 2px;
        height: 30px;
        background: #909399;
        border-radius: 1px;
      }
      &:hover::before { background: white; }
    }

    .left-panel {
      flex-shrink: 0;
      border-right: none;
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0; // 重要：允许flex子元素缩小
      
      .list-header {
        padding: 1rem;
        border-bottom: 1px solid #e4e7ed;
        display: flex;
        align-items: center;
      }
      
      .table-list-wrapper {
        flex: 1;
        overflow: auto; // 允许滚动查看表格内容
        padding: 0.5rem;
        display: flex;
        flex-direction: column;
        
        :deep(.dynamic-table-card) {
          box-shadow: none;
          border: none;
          height: 100%;
          display: flex;
          flex-direction: column;
          
          .el-card__body {
            flex: 1;
            height: 0;
            overflow: auto; // 允许滚动
            display: flex;
            flex-direction: column;
          }
        }
        
        :deep(.el-table) {
          height: 100%;
          width: max-content; // 表格宽度根据内容自适应
          min-width: 100%; // 最小宽度为容器宽度
          
          .el-table__body-wrapper {
            flex: 1;
            height: 0;
          }
          
          .el-table__row {
            cursor: pointer;
            
            &:hover {
              background-color: #f5f7fa;
            }
            
            &.current-row {
              background-color: #ecf5ff;
              
              td {
                background-color: #ecf5ff;
              }
            }
          }
          
          // 操作列按钮样式
          .el-table__body-wrapper {
            .el-button--danger {
              padding: 5px 10px;
              min-height: auto;
              
              .el-icon {
                display: inline-flex;
                align-items: center;
                justify-content: center;
              }
            }
          }
        }
        
        .empty-list {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 200px;
          color: #909399;
          font-size: 14px;
        }
      }
      
      .table-list {
        flex: 1;
        padding: 0.5rem;
        background: transparent;

        .table-item {
          margin-bottom: 0.5rem;
          border-radius: 8px;
          border: 2px solid transparent;
          background: white;
          transition: all 0.3s ease;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

          &:hover {
            border-color: #409eff;
            box-shadow: 0 4px 8px rgba(64, 158, 255, 0.2);
            transform: translateY(-2px);
          }

          &.active {
            border-color: #409eff;
            background: linear-gradient(135deg, #409eff 0%, #79bbff 100%);
            color: white;
            box-shadow: 0 6px 12px rgba(64, 158, 255, 0.3);


            .delete-btn {
              color: white;
              border-color: rgba(255, 255, 255, 0.3);

              &:hover {
                background: rgba(255, 255, 255, 0.1);
                border-color: rgba(255, 255, 255, 0.5);
              }
            }
          }


          .empty-list {
            display: flex;
            align-items: center;
            justify-content: center;
            height: 200px;
            color: #909399;
            font-size: 14px;

            :deep(.el-empty__description) {
              color: #909399;
              font-size: 14px;
            }
          }
        }
      }
    }

    // 右侧编辑区域
    .right-panel {
      flex: 1;
      display: flex;
      flex-direction: column;
      padding: 1rem;
      min-width: 0; // 防止flex子元素溢出
      min-height: 0; // 重要：允许flex子元素缩小
      overflow: hidden; // 防止内容溢出到父容器
      
      .edit-area {
        flex: 1;
        height: 0; // 配合flex: 1使用，确保高度正确
        min-height: 0; // 重要：允许flex子元素缩小
        display: flex;
        flex-direction: column;
        min-width: 0; // 防止flex子元素溢出
        overflow-y: auto; // 统一右侧滚动条
        overflow-x: hidden;
        scrollbar-width: thin;
        scrollbar-color: rgba(144, 147, 153, 0.4) transparent;
      }
      
      .empty-state {
        flex: 1;
        height: 0; // 配合flex: 1使用，确保高度正确
        min-height: 0; // 重要：允许flex子元素缩小
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
      }
      
      // 编辑模式控制区域
      .edit-mode-control {
        margin-bottom: 1rem;
        padding: 0.75rem;
        background: white;
        border-radius: 8px;
        border: 1px solid #e4e7ed;
        display: flex;
        align-items: center;
      }
      
      .toolbar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-bottom: 1rem;
        margin-bottom: 1rem;
        border-bottom: 1px solid #e4e7ed;
        
        .toolbar-left {
          display: flex;
          align-items: center;
          gap: 10px;
          
          .toolbar-tip {
            font-size: 12px;
            color: #909399;
            margin-left: 10px;
          }
        }
      }
      
      // 名称输入区域 - 使用 Grid 布局实现换行对齐
      .table-name-section {
        margin-bottom: 0.75rem;
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: 12px 16px;
        
        .form-item {
          display: flex;
          align-items: center;
          gap: 6px;
          min-width: 0; // 防止内容溢出
        }

        .form-item--date {
          align-items: flex-start;
        }

        .form-item--remark {
          grid-column: 1 / -1;
          align-items: flex-start;
        }

        .form-control {
          flex: 1;
          min-width: 0;
        }

        .section-label {
          font-size: 14px;
          color: #606266;
          font-weight: 500;
          white-space: nowrap;
          flex-shrink: 0;
          width: 70px; // 固定标签宽度，确保对齐
          text-align: right;
        }

        .section-label--required::before {
          content: '*';
          color: #f56c6c;
          margin-right: 4px;
        }
        
        // 输入框和选择器占满剩余空间
        .el-input, .el-select, .el-date-picker {
          flex: 1;
          min-width: 0;
        }

        .form-control .el-date-picker {
          width: 100%;
        }

        .form-item--error :deep(.el-input__wrapper) {
          box-shadow: 0 0 0 1px #f56c6c inset;
        }

        .form-error-text {
          margin-top: 4px;
          color: #f56c6c;
          font-size: 12px;
          line-height: 1;
        }
      }
      
      // 特殊属性区域与图片上传区域容器 - 流式布局
      .attributes-container {
        display: flex;
        flex-wrap: wrap;
        gap: 20px;
        align-items: flex-start;
        margin-bottom: 0.75rem;
      }

      .axis-placeholder-tip {
        margin: 0 0 0.75rem;
        padding: 8px 12px;
        color: #606266;
        font-size: 13px;
        line-height: 1.5;
        background: #f4f8ff;
        border-left: 3px solid #409eff;
        border-radius: 4px;
      }
      
      // 非数据表区域字段配置
      .special-attributes {
        flex: 1;
        min-width: 300px;
        margin-top: 0;
        margin-bottom: 0;
        padding: 1rem;
        background: white;
        border-radius: 8px;
        border: 1px solid #e4e7ed;

        .attributes-title {
          font-size: 16px;
          font-weight: 600;
          color: #606266;
          margin-bottom: 0.5rem;
          padding-bottom: 0.5rem;
        }

        .attributes-subtitle {
          font-size: 13px;
          color: #909399;
          margin-bottom: 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid #ebeef5;
        }

        .attributes-columns {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;

          .attributes-column {
            flex: 1;
            min-width: 260px;
            padding: 12px;
            background: #f8f9fa;
            border-radius: 8px;
            border: 1px solid #ebeef5;

            .column-title {
              display: flex;
              align-items: center;
              gap: 6px;
              font-size: 14px;
              font-weight: 600;
              color: #303133;
              margin-bottom: 6px;

              .el-icon {
                color: #409eff;
              }
            }

            .column-desc {
              font-size: 12px;
              color: #909399;
              margin-bottom: 12px;
              line-height: 1.5;
            }

            &.global-attributes {
              background: #f0f9ff;
              border-color: #c6e2ff;
            }

            &.single-attributes {
              background: #f6ffed;
              border-color: #b7eb8f;
            }
          }
        }

        .attributes-list {
          margin-bottom: 1rem;

          .attribute-item {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 0.5rem;
            margin-bottom: 0.75rem;
            padding: 0.5rem;
            background: #fafafa;
            border-radius: 6px;
            transition: all 0.3s ease;
            overflow: hidden; // 防止内容溢出
            cursor: grab;

            &:hover {
              background: #f0f0f0;
              box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            }

            &.dragging {
              opacity: 0.5;
              background: #e6f7ff;
              cursor: grabbing;
            }

            &.drag-over {
              border: 2px dashed #409eff;
              background: #ecf5ff;
            }

            .drag-handle {
              flex-shrink: 0;
              cursor: grab;
              color: #909399;
              padding: 4px;

              &:hover {
                color: #409eff;
              }

              &:active {
                cursor: grabbing;
              }
            }

            .attribute-label {
              flex-shrink: 0;
              width: 50px;
              min-width: 50px;
              font-size: 14px;
              color: #606266;
              font-weight: 500;
              text-align: right;
            }

            .attribute-input {
              flex: 0 0 auto;
              min-width: 120px;
            }

            .attribute-options-display {
              display: flex;
              align-items: center;
              gap: 6px;
              flex: 1;
              margin-left: 12px;
              flex-wrap: wrap;

              .options-label {
                font-size: 13px;
                color: #909399;
                flex-shrink: 0;
              }

              .option-tag {
                margin: 2px;
              }
            }

            .attribute-delete-btn {
              flex-shrink: 0;
              transition: all 0.3s ease;

              &:hover {
                transform: scale(1.1);
              }
            }

            .attribute-options-btn {
              flex-shrink: 0;
              margin-left: 8px;
            }

            .attribute-switch-btn {
              flex-shrink: 0;
              margin-left: auto;
              font-size: 12px;
              color: #909399;

              &:hover {
                color: #409eff;
              }
            }
          }
        }

        .add-attribute-btn {
          width: 100%;
          border-radius: 6px;
        }
      }

      // 特殊属性选项编辑弹框样式
      .options-edit-container {
        .option-item {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 10px;
          
          .el-input {
            flex: 1;
          }
        }
      }
      
      .table-container {
        flex: 1 1 auto;
        width: 100%;
        max-width: 100%;
        min-height: 300px;
        height: 100%;
        overflow-x: auto;
        overflow-y: auto;
        box-sizing: border-box;
        padding-bottom: 0;
        scrollbar-width: thin;
        scrollbar-color: #409eff #f0f2f5;
        
        // 表格包装器（为分区名称提供定位上下文）
        .table-wrapper {
          position: relative;
          display: inline-block; // 宽度跟随表格内容
          min-width: 100%;
          padding-top: 28px; // 为分区名称标签留出空间
          
          // 分区名称浮动层（独立于表格层叠上下文）
          .section-names-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 28px;
            pointer-events: none;
            z-index: 100;
            
            .section-name-label {
              position: absolute;
              top: 4px;
              background-color: #1a5490;
              color: #fff;
              font-size: 12px;
              padding: 2px 8px;
              border-radius: 3px;
              white-space: nowrap;
              font-weight: normal;
              pointer-events: auto;
              
              &::after {
                content: '';
                position: absolute;
                bottom: -4px;
                left: 50%;
                transform: translateX(-50%);
                border-left: 4px solid transparent;
                border-right: 4px solid transparent;
                border-top: 4px solid #1a5490;
              }
            }
          }
        }

        .editable-table {
          width: max-content; // 根据内容计算宽度
          min-width: 100%; // 最小宽度为容器宽度
          border-collapse: collapse;
          border: 1px solid #e4e7ed;
          background: white;
          margin-bottom: auto; // 表格内容少时推到顶部，滚动条在底部

          .corner-header {
            position: relative;
            width: 120px;
            min-width: 120px;
            padding: 0;
            border: 1px solid #e4e7ed;
            background: #f0f0f0;
            font-weight: bold;
            text-align: center;
            vertical-align: middle;
            font-size: 14px;
            color: #606266;
            
            .column-border-btn {
              position: absolute;
              top: 0;
              right: -3.5px;
              z-index: 10;
              width: 4px;
              height: 100%;
              background-color: #000000;
              cursor: pointer;
              transition: all 0.3s ease;
              
              &:hover {
                background-color: #333333;
              }
            }
          }

          .table-header {
            position: relative;
            width: 250px;
            min-width: 250px;
            padding: 0;
            border: 1px solid #e4e7ed;
            background: #fafafa;

            .header-input {
              padding-right: 158px;
              
              :deep(.el-input__inner) {
                border: none;
                background: transparent;
                font-weight: bold;
                text-align: center;
                padding-right: 0;
              }
            }

            .select-options-btn {
              position: absolute;
              top: 4px;
              right: 116px;
            }
            
            .column-type-btn {
              position: absolute;
              top: 4px;
              right: 60px;
            }

            .delete-column-btn {
              position: absolute;
              top: 4px;
              right: 8px;
            }
            
            .column-border-btn {
              position: absolute;
              top: 0;
              right: -3.5px;
              z-index: 10;
              width: 4px;
              height: 100%;
              background-color: #000000;
              cursor: pointer;
              transition: all 0.3s ease;
              
              &:hover {
                background-color: #333333;
              }
              
              &.active {
                background-color: #000000;
                
                &:hover {
                  background-color: #333333;
                }
              }
            }
            
            &.has-border {
              border-right: 3px solid #1a5490 !important;
            }
          }

          .vertical-header-cell {
            width: 120px;
            min-width: 120px;
            padding: 0;
            border: 1px solid #e4e7ed;
            background: #fafafa;

            .vertical-header-input {
              :deep(.el-input__inner) {
                border: none;
                background: transparent;
                font-weight: bold;
                text-align: center;
              }
            }
          }

          .data-cell {
            width: 150px;
            min-width: 150px;
            padding: 0;
            border: 1px solid #e4e7ed;
            
            &.has-border {
              border-right: 3px solid #1a5490 !important;
            }

            .el-input {
              width: 100%;

              :deep(.el-input__inner) {
                border: none;
                border-radius: 0;
              }
            }

            .preset-value-input {
              :deep(.el-input__inner) {
                text-align: center;
              }
            }
            
            .cell-checkbox {
              width: 100%;
              display: flex;
              justify-content: center;
              align-items: center;
              padding: 8px;
              
              :deep(.el-checkbox) {
                display: flex;
                justify-content: center;
                align-items: center;
                margin: 0;
              }
              
              :deep(.el-checkbox__input) {
                display: flex;
                justify-content: center;
                align-items: center;
                
                .el-checkbox__inner {
                  width: 18px;
                  height: 18px;
                  border-radius: 2px;
                  position: relative;
                  
                  &::after {
                    // 确保勾选标记居中
                    position: absolute;
                    left: 50%;
                    top: 50%;
                    transform: translate(-50%, -60%) rotate(45deg) scaleY(1);
                    margin-left: 0;
                    margin-top: 0;
                  }
                }
              }
              
              :deep(.el-checkbox__label) {
                display: none;
              }
            }
            
            .el-select {
              width: 100%;
              
              :deep(.el-input__inner) {
                border: none;
                border-radius: 0;
              }
            }
          }

          .row-control {
            width: 50px;
            min-width: 50px;
            text-align: center;
            vertical-align: middle;
            background: #fafafa;
          }
        }

        td.auto-fill-group-start {
          border-top: 3px solid #1a5490 !important;
        }
      }

      :deep(.edit-area::-webkit-scrollbar),
      :deep(.table-container::-webkit-scrollbar) {
        width: 8px;
        height: 8px;
      }

      :deep(.edit-area::-webkit-scrollbar-track),
      :deep(.table-container::-webkit-scrollbar-track) {
        background: transparent;
      }

      :deep(.edit-area::-webkit-scrollbar-thumb),
      :deep(.table-container::-webkit-scrollbar-thumb) {
        background-color: rgba(144, 147, 153, 0.5);
        border-radius: 6px;
      }

      :deep(.edit-area::-webkit-scrollbar-thumb:hover),
      :deep(.table-container::-webkit-scrollbar-thumb:hover) {
        background-color: rgba(64, 158, 255, 0.7);
      }
    }
  }
  
  // 列类型对话框样式
  .column-type-options {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 14px;
    padding: 4px 0 8px;
    
    .column-type-item {
      grid-column: span 2;
      min-height: 132px;
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 18px 12px;
      border: 2px solid #e4e7ed;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.3s ease;
      background: white;
      
      &:hover {
        border-color: #409eff;
        background: #f0f9ff;
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(64, 158, 255, 0.2);
      }
      
      .type-icon {
        font-size: 32px;
        color: #409eff;
        margin-bottom: 0.5rem;
      }
      
      .type-label {
        font-size: 15px;
        color: #606266;
        font-weight: 500;
      }
      
      &.active {
        border-color: #409eff;
        background: #ecf5ff;
        
        .type-icon {
          color: #409eff;
        }
        
        .type-label {
          color: #409eff;
          font-weight: 600;
        }
      }
      
      .check-icon {
        position: absolute;
        top: 8px;
        right: 8px;
        font-size: 20px;
        color: #409eff;
      }

      &:nth-child(4) {
        grid-column: 2 / span 2;
      }

      &:nth-child(5) {
        grid-column: 4 / span 2;
      }
    }
  }
  
  // 下拉选项编辑对话框样式
  .select-options-editor {
    .preset-value-tip {
      margin-bottom: 12px;
      padding: 8px 12px;
      color: #606266;
      font-size: 13px;
      line-height: 1.5;
      background: #f4f8ff;
      border-left: 3px solid #409eff;
      border-radius: 4px;
    }

    .options-list {
      margin-bottom: 1rem;
      max-height: 400px;
      overflow-y: auto;
      
      .option-item {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 0.75rem;
        padding: 0.5rem;
        background: #fafafa;
        border-radius: 6px;
        transition: all 0.3s ease;
        
        &:hover {
          background: #f0f0f0;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        
        .el-input {
          flex: 1;
        }

        .option-main {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .option-upload {
          flex-shrink: 0;
        }

        .option-image-preview {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
          padding: 6px 8px;
          border: 1px solid #d9ecff;
          border-radius: 4px;
          background: #f5faff;

          .option-thumb {
            width: 42px;
            height: 42px;
            flex-shrink: 0;
            border: 1px solid #dcdfe6;
            border-radius: 4px;
            overflow: hidden;
            background: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: zoom-in;

            img {
              width: 100%;
              height: 100%;
              object-fit: cover;
            }

            .el-icon {
              color: #909399;
              font-size: 20px;
            }
          }

          .option-image-info {
            min-width: 0;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .option-image-name {
            min-width: 0;
            max-width: 220px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            color: #606266;
            font-size: 13px;
          }
        }
        
        .el-button {
          flex-shrink: 0;
        }
      }
    }
    
    .add-option-btn {
      width: 100%;
      border-radius: 6px;
    }
  }

  .select-option-image-preview-dialog {
    min-height: 360px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f7fa;
    border-radius: 6px;
    overflow: hidden;

    img {
      max-width: 100%;
      max-height: 70vh;
      object-fit: contain;
      background: #fff;
    }

    .preview-loading {
      color: #909399;
      font-size: 14px;
    }
  }
  
  .image-upload-section {
    flex-shrink: 0;
    width: 320px;
    min-width: 280px;
    padding: 10px;
    background: white;
    border-radius: 8px;
    border: 1px solid #e4e7ed;
    
    .upload-progress-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 20px;
      
      .progress-text {
        font-size: 14px;
        font-weight: bold;
        color: #409eff;
      }
      
      .progress-label {
        margin-top: 10px;
        font-size: 12px;
        color: #909399;
      }
    }
    
    :deep(.el-upload-dragger) {
      padding: 10px;
      height: 120px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      
      .el-icon--upload {
        margin-bottom: 8px;
        font-size: 48px;
      }
      
      .el-upload__text {
        font-size: 12px;
      }
    }
  }
}
</style>
